import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiUser,
  FiDollarSign,
  FiCalendar,
  FiUsers,
  FiSettings,
  FiEdit,
  FiPlus,
  FiTrash2,
  FiClock,
  FiVideo,
  FiEye,
  FiSave,
  FiLinkedin,
  FiMail,
  FiPhone,
  FiGlobe,
  FiStar,
  FiPieChart,
  FiTrendingUp,
  FiCheck,
  FiX,
  FiBriefcase,
  FiAlertCircle,
  FiLink,
  FiCopy,
  FiMessageCircle,
  FiRefreshCw,
  FiUpload,
  FiDownload,
  FiAlertTriangle,
  FiRepeat,
  FiRotateCcw,
  FiInfo,
  FiMaximize2,
  FiMinimize2,
  FiFileText,
  FiHome,
  FiShare2,
  FiChevronRight,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import InfoTooltip from '../../components/InfoTooltip';
import ProfilePicture from '../../components/ProfilePicture';
import { Modal, Button, Form, Alert, Card, Badge } from 'react-bootstrap';
import {
  API_BASE_URL,
  toastdisplay,
  saveCoachToLocalStorage,
  fetchConversations,
  getAuthHeaders,
} from '../../utils/utils';
import { getStoredUser } from '../../utils/authSession';
import {
  generateCoachProfileUrl,
  generateCoachInviteUrl,
} from '../../utils/hashids';
import Sidebar from '../Sidebar';
import DocumentReviewQueue from './DocumentReviewQueue';
import { CallMessaging } from '../messaging/Messaging';
import './CoachDashboard.css';

const CoachDashboard = () => {
  const navigate = useNavigate();
  const [user] = useState(() => getStoredUser());

  // Check if user has coach role
  useEffect(() => {
    if (!user || user.role?.name !== 'coach') {
      //navigate('/community');
      return;
    }

    // Fetch conversations for messaging
    const loadConversations = async () => {
      // Prevent duplicate conversations loading
      if (isLoadingConversationsRef.current) {
        return;
      }

      try {
        isLoadingConversationsRef.current = true;
        //console.log('Fetching conversations from backend...');
        const groupedConversations = await fetchConversations(user.email);
        setConversations(groupedConversations);
      } catch (err) {
        console.error('Error fetching conversations:', err);
        setConversations([]);
      } finally {
        isLoadingConversationsRef.current = false;
      }
    };

    loadConversations();
  }, [user, navigate]);

  // State management
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(false);

  // Refs to prevent duplicate API calls
  const isLoadingCoachDataRef = useRef(false);
  const isLoadingCoachProfileRef = useRef(false);
  const isLoadingDashboardDataRef = useRef(false);
  const isLoadingReferralsRef = useRef(false);
  const isLoadingConversationsRef = useRef(false);
  const isLoadingSchoolsRef = useRef(false);

  // Referrals state
  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralFilter, setReferralFilter] = useState('all');

  // Coach state management
  const [coachProfile, setCoachProfile] = useState({
    id: '',
    name: '',
    email: '',
    profileImage: '',
    tagline: '',
    about: '',
    whyCoach: '',
    linkedin: '',
    website: '',
    responseTime: '1 hour',
    timezone: 'UTC',
    minBookingNotice: 1, // Minimum days in advance for booking (buffer time)
    education: [],
    workExperience: [],
    services: [],
    reviews: [],
    specializedSchools: [],
    sectorsOfInterest: [],
    expertises: [],
    lookingToMeet: [],
  });

  const [services, setServices] = useState([]);
  const [serviceFilter, setServiceFilter] = useState('active'); // 'active', 'inactive', 'all'
  const [calendar, setCalendar] = useState({
    availability: {},
    blockedDates: [],
  });
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState('monday');
  const [newTimeSlot, setNewTimeSlot] = useState({
    start: '',
    end: '',
  });
  const [bookings, setBookings] = useState([]);
  const [bookingFilter, setBookingFilter] = useState('confirmed'); // Default to confirmed bookings
  const [bookingServiceFilter, setBookingServiceFilter] = useState('all');
  const [bookingDateFilter, setBookingDateFilter] = useState('all');
  const [bookingTimeSlotFilter, setBookingTimeSlotFilter] = useState('all');
  const [earnings, setEarnings] = useState({
    totalRevenue: 0,
    platformFee: 0,
    vat: 0,
    netEarnings: 0,
    pendingPayouts: 0,
    thisMonth: 0,
    thisWeek: 0,
    paidOut: 0,
    transactions: [],
  });
  const [reviews, setReviews] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [payoutForm, setPayoutForm] = useState({
    payout_method: '',
    payout_details: '',
    review_sla_business_days: 3,
  });
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [introForm, setIntroForm] = useState({
    title: 'Intro 30 min',
    price: '',
    isFree: true,
  });
  const [sharedHours, setSharedHours] = useState({
    start: '09:00',
    end: '17:00',
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  });
  const [agendaRange, setAgendaRange] = useState('7d');
  const [relectureCount, setRelectureCount] = useState(0);

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceModalFullscreen, setServiceModalFullscreen] = useState(false);
  const [serviceModalStep, setServiceModalStep] = useState('details');
  const [showAvailabilityAdvanced, setShowAvailabilityAdvanced] =
    useState(false);
  const [showServiceMoreDetails, setShowServiceMoreDetails] = useState(false);
  const [quickHours, setQuickHours] = useState({ start: '09:00', end: '17:00' });
  const [editingService, setEditingService] = useState(null);
  const [selectedCopyDay, setSelectedCopyDay] = useState(null);
  const [selectedWeekOffset, setSelectedWeekOffset] = useState(0); // 0 = recurring, 1-8 = specific weeks ahead
  const [newService, setNewService] = useState({
    title: '',
    description: '',
    duration: 60,
    restTime: 0, // Buffer time between successive meetings (in minutes)
    price: '',
    priceUnit: 'per session',
    type: 'individual',
    isFree: false,
    maxParticipants: 1,
    is_active: true, // Default new services to active
    availability: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    },
    // Week-specific availability (keys are week offsets: 1, 2, 3, etc.)
    weeklyOverrides: {},
    // Excluded individual slots per day (e.g., { monday: ["17:00-17:10", "17:20-17:30"] })
    excludedSlots: {},
  });
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [conversations, setConversations] = useState([]);
  const [showMessagingPanel, setShowMessagingPanel] = useState(false);
  const [
    selectedConversationForMessaging,
    setSelectedConversationForMessaging,
  ] = useState(null);

  // Message modal state for viewing and replying to student messages
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedBookingForMessage, setSelectedBookingForMessage] =
    useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // Calendar sync state
  const [importedCalendarEvents, setImportedCalendarEvents] = useState([]);
  const [calendarFileName, setCalendarFileName] = useState('');
  const [calendarSyncLoading, setCalendarSyncLoading] = useState(false);
  const [gcalStatus, setGcalStatus] = useState(null);
  const [gcalBusy, setGcalBusy] = useState(false);
  const calendarInputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Profile validation errors state
  const [profileValidationErrors, setProfileValidationErrors] = useState([]);

  // Available schools from localStorage
  const [availableSchools, setAvailableSchools] = useState(
    JSON.parse(localStorage.getItem('availableschools')) ||
      JSON.parse(localStorage.getItem('schools')) ||
      []
  );

  // iCalendar parser function
  const parseICalendar = (icsContent) => {
    const events = [];
    const lines = icsContent.split(/\r?\n/);
    let currentEvent = null;
    let lastKey = '';

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];

      // Handle line continuations (lines starting with space or tab)
      if (line.startsWith(' ') || line.startsWith('\t')) {
        if (currentEvent && lastKey) {
          currentEvent[lastKey] += line.substring(1);
        }
        continue;
      }

      if (line.startsWith('BEGIN:VEVENT')) {
        currentEvent = {};
      } else if (line.startsWith('END:VEVENT') && currentEvent) {
        // Parse the event dates
        const startDate = parseICalDate(currentEvent.DTSTART);
        const endDate = parseICalDate(currentEvent.DTEND);

        if (startDate && endDate) {
          events.push({
            id: currentEvent.UID || `event-${Date.now()}-${events.length}`,
            summary: currentEvent.SUMMARY || 'Busy',
            start: startDate,
            end: endDate,
            isAllDay:
              currentEvent.DTSTART?.includes('VALUE=DATE') ||
              (!currentEvent.DTSTART?.includes('T') &&
                currentEvent.DTSTART?.length === 8),
          });
        }
        currentEvent = null;
      } else if (currentEvent) {
        const colonIndex = line.indexOf(':');
        if (colonIndex > 0) {
          let key = line.substring(0, colonIndex);
          const value = line.substring(colonIndex + 1);

          // Handle keys with parameters like DTSTART;VALUE=DATE
          if (key.includes(';')) {
            const keyParts = key.split(';');
            key = keyParts[0];
            // Store the full key with parameters for date parsing
            currentEvent[key + '_PARAMS'] = keyParts.slice(1).join(';');
          }

          currentEvent[key] = value;
          lastKey = key;
        }
      }
    }

    return events;
  };

  // Parse iCalendar date formats
  const parseICalDate = (dateStr) => {
    if (!dateStr) return null;

    try {
      // Remove any timezone identifier suffix
      let cleanDate = dateStr;

      // Handle TZID parameter format: TZID=Europe/Paris:20231215T140000
      if (dateStr.includes(':')) {
        cleanDate = dateStr.split(':').pop();
      }

      // Format: YYYYMMDD (all-day event)
      if (cleanDate.length === 8 && !cleanDate.includes('T')) {
        const year = parseInt(cleanDate.substring(0, 4));
        const month = parseInt(cleanDate.substring(4, 6)) - 1;
        const day = parseInt(cleanDate.substring(6, 8));
        return new Date(year, month, day);
      }

      // Format: YYYYMMDDTHHmmss or YYYYMMDDTHHmmssZ
      if (cleanDate.includes('T')) {
        const datePart = cleanDate.substring(0, 8);
        const timePart = cleanDate.substring(9, 15);

        const year = parseInt(datePart.substring(0, 4));
        const month = parseInt(datePart.substring(4, 6)) - 1;
        const day = parseInt(datePart.substring(6, 8));
        const hour = parseInt(timePart.substring(0, 2));
        const minute = parseInt(timePart.substring(2, 4));
        const second = parseInt(timePart.substring(4, 6)) || 0;

        // If ends with Z, it's UTC
        if (cleanDate.endsWith('Z')) {
          return new Date(Date.UTC(year, month, day, hour, minute, second));
        }

        return new Date(year, month, day, hour, minute, second);
      }

      return null;
    } catch (error) {
      console.error('Error parsing iCal date:', dateStr, error);
      return null;
    }
  };

  // Check if a time slot conflicts with imported calendar events
  // Only shows conflicts for the specific day of week, consolidated by time range
  const checkTimeSlotConflict = (day, startTime, endTime) => {
    if (importedCalendarEvents.length === 0)
      return { hasConflict: false, conflictingEvents: [] };

    const conflictingEvents = [];
    const daysOfWeek = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    const targetDayIndex = daysOfWeek.indexOf(day.toLowerCase());

    // Get current date and next 8 weeks to check for conflicts
    const today = new Date();
    const endCheckDate = new Date(today);
    endCheckDate.setDate(endCheckDate.getDate() + 56); // Check 8 weeks ahead

    // Track unique conflicts by time range to avoid duplicates
    const seenTimeRanges = new Map();

    for (const event of importedCalendarEvents) {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);

      // Skip past events
      if (eventEnd < today) continue;

      // Skip events too far in the future
      if (eventStart > endCheckDate) continue;

      // Check if event falls on the target day of week
      const eventDayOfWeek = eventStart.getDay();
      if (eventDayOfWeek !== targetDayIndex) continue;

      // For all-day events, always conflict
      if (event.isAllDay) {
        const key = 'all-day';
        if (!seenTimeRanges.has(key)) {
          seenTimeRanges.set(key, {
            ...event,
            date: eventStart.toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
            }),
            time: 'All day',
          });
        }
        continue;
      }

      // Check time overlap
      const eventStartTime = `${String(eventStart.getHours()).padStart(2, '0')}:${String(eventStart.getMinutes()).padStart(2, '0')}`;
      const eventEndTime = `${String(eventEnd.getHours()).padStart(2, '0')}:${String(eventEnd.getMinutes()).padStart(2, '0')}`;

      // Check if times overlap with the availability window
      if (!(endTime <= eventStartTime || startTime >= eventEndTime)) {
        // Use time range as key to consolidate duplicates
        const key = `${eventStartTime}-${eventEndTime}`;
        if (!seenTimeRanges.has(key)) {
          seenTimeRanges.set(key, {
            ...event,
            date: eventStart.toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
            }),
            time: `${eventStartTime} - ${eventEndTime}`,
          });
        }
      }
    }

    // Convert map values to array
    const uniqueConflicts = Array.from(seenTimeRanges.values());

    return {
      hasConflict: uniqueConflicts.length > 0,
      conflictingEvents: uniqueConflicts,
    };
  };

  // Check if a time slot conflicts with other active services' availability
  // Only checks for the specific day of the specific week (selectedWeekOffset)
  // - If selectedWeekOffset === 0 (recurring), only check recurring availability of other services
  // - If selectedWeekOffset > 0 (week-specific), only check that same week of other services
  const checkServiceConflict = (day, startTime, endTime) => {
    const conflictingServices = [];

    // Get all other active services (exclude the one being edited)
    const otherActiveServices = services.filter(
      (service) =>
        service.is_active &&
        (!editingService || service.id !== editingService.id)
    );

    const dayLower = day.toLowerCase();

    for (const service of otherActiveServices) {
      const availability = service.availability;
      if (!availability) continue;

      let slotsToCheck = [];
      const weeklyOverrides =
        service.weeklyOverrides || service.weekly_overrides || {};

      if (selectedWeekOffset === 0) {
        // Checking recurring availability - only check recurring of other services
        // Handle new "recurring" structure: { recurring: { monday: [...], ... } }
        if (availability.recurring && availability.recurring[dayLower]) {
          slotsToCheck = [...slotsToCheck, ...availability.recurring[dayLower]];
        }
        // Handle direct day-based format: { monday: [...], ... }
        if (availability[dayLower] && Array.isArray(availability[dayLower])) {
          slotsToCheck = [...slotsToCheck, ...availability[dayLower]];
        }
      } else {
        // Checking week-specific availability - only check same week of other services
        if (
          weeklyOverrides[selectedWeekOffset] &&
          weeklyOverrides[selectedWeekOffset][dayLower] &&
          Array.isArray(weeklyOverrides[selectedWeekOffset][dayLower])
        ) {
          slotsToCheck = [
            ...slotsToCheck,
            ...weeklyOverrides[selectedWeekOffset][dayLower],
          ];
        }
        // Also check raw format (week_1, week_2, etc.) in case not parsed
        const weekKey = `week_${selectedWeekOffset}`;
        if (
          availability[weekKey] &&
          availability[weekKey][dayLower] &&
          Array.isArray(availability[weekKey][dayLower])
        ) {
          slotsToCheck = [
            ...slotsToCheck,
            ...availability[weekKey][dayLower],
          ];
        }
      }

      // Check for time overlap with each slot
      for (const slot of slotsToCheck) {
        const slotStart = slot.start || slot.start_time;
        const slotEnd = slot.end || slot.end_time;

        if (!slotStart || !slotEnd) continue;

        // Check if times overlap
        if (!(endTime <= slotStart || startTime >= slotEnd)) {
          conflictingServices.push({
            serviceId: service.id,
            serviceTitle: service.title,
            time: `${slotStart} - ${slotEnd}`,
            day: dayLower,
          });
        }
      }
    }

    // Remove duplicate conflicts from the same service (by service + time)
    const uniqueConflicts = conflictingServices.reduce((acc, conflict) => {
      const existingIndex = acc.findIndex(
        (c) => c.serviceId === conflict.serviceId && c.time === conflict.time
      );
      if (existingIndex === -1) {
        acc.push(conflict);
      }
      return acc;
    }, []);

    return {
      hasConflict: uniqueConflicts.length > 0,
      conflictingServices: uniqueConflicts,
    };
  };

  // Combined conflict check for both calendar and services
  const checkAllConflicts = (day, startTime, endTime) => {
    const calendarConflict = checkTimeSlotConflict(day, startTime, endTime);
    const serviceConflict = checkServiceConflict(day, startTime, endTime);

    return {
      hasCalendarConflict: calendarConflict.hasConflict,
      conflictingEvents: calendarConflict.conflictingEvents,
      hasServiceConflict: serviceConflict.hasConflict,
      conflictingServices: serviceConflict.conflictingServices,
      hasAnyConflict:
        calendarConflict.hasConflict || serviceConflict.hasConflict,
    };
  };

  // Calculate free time portions within an availability window
  // This shows which time slots are NOT conflicting with other services
  const calculateFreeTimePortions = (
    day,
    startTime,
    endTime,
    serviceDuration = 10,
    restTime = 0
  ) => {
    // Total slot spacing includes rest time between sessions
    const slotSpacing = serviceDuration + restTime;
    // Get all conflicting time ranges (from both services AND calendar events)
    const conflictingRanges = [];
    const dayLower = day.toLowerCase();

    // 1. Add conflicts from other active services
    const otherActiveServices = services.filter(
      (service) =>
        service.is_active &&
        (!editingService || service.id !== editingService.id)
    );

    for (const service of otherActiveServices) {
      const availability = service.availability;
      if (!availability) continue;

      let slotsToCheck = [];
      const weeklyOverrides =
        service.weeklyOverrides || service.weekly_overrides || {};

      if (selectedWeekOffset === 0) {
        // Checking recurring availability - only check recurring of other services
        // Handle new "recurring" structure: { recurring: { monday: [...], ... } }
        if (availability.recurring && availability.recurring[dayLower]) {
          slotsToCheck = [...slotsToCheck, ...availability.recurring[dayLower]];
        }
        // Handle direct day-based format: { monday: [...], ... }
        if (availability[dayLower] && Array.isArray(availability[dayLower])) {
          slotsToCheck = [...slotsToCheck, ...availability[dayLower]];
        }
      } else {
        // Checking week-specific availability - only check same week of other services
        if (
          weeklyOverrides[selectedWeekOffset] &&
          weeklyOverrides[selectedWeekOffset][dayLower] &&
          Array.isArray(weeklyOverrides[selectedWeekOffset][dayLower])
        ) {
          slotsToCheck = [
            ...slotsToCheck,
            ...weeklyOverrides[selectedWeekOffset][dayLower],
          ];
        }
        // Also check raw format (week_1, week_2, etc.) in case not parsed
        const weekKey = `week_${selectedWeekOffset}`;
        if (
          availability[weekKey] &&
          availability[weekKey][dayLower] &&
          Array.isArray(availability[weekKey][dayLower])
        ) {
          slotsToCheck = [
            ...slotsToCheck,
            ...availability[weekKey][dayLower],
          ];
        }
      }

      // Add all slots from this service as conflicts
      slotsToCheck.forEach((slot) => {
        const slotStart = slot.start || slot.start_time;
        const slotEnd = slot.end || slot.end_time;
        if (slotStart && slotEnd) {
          conflictingRanges.push({ start: slotStart, end: slotEnd });
        }
      });
    }

    // 2. Add conflicts from imported calendar events
    if (importedCalendarEvents.length > 0) {
      const daysOfWeek = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ];
      const targetDayIndex = daysOfWeek.indexOf(day.toLowerCase());

      const today = new Date();
      const endCheckDate = new Date(today);
      endCheckDate.setDate(endCheckDate.getDate() + 56); // Check 8 weeks ahead

      for (const event of importedCalendarEvents) {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);

        // Skip past events
        if (eventEnd < today) continue;
        // Skip events too far in the future
        if (eventStart > endCheckDate) continue;
        // Check if event falls on the target day of week
        const eventDayOfWeek = eventStart.getDay();
        if (eventDayOfWeek !== targetDayIndex) continue;

        // For all-day events, block the entire window
        if (event.isAllDay) {
          conflictingRanges.push({ start: startTime, end: endTime });
          continue;
        }

        // Extract event times
        const eventStartTime = `${String(eventStart.getHours()).padStart(2, '0')}:${String(eventStart.getMinutes()).padStart(2, '0')}`;
        const eventEndTime = `${String(eventEnd.getHours()).padStart(2, '0')}:${String(eventEnd.getMinutes()).padStart(2, '0')}`;

        // Check if event overlaps with our time window
        if (!(endTime <= eventStartTime || startTime >= eventEndTime)) {
          conflictingRanges.push({ start: eventStartTime, end: eventEndTime });
        }
      }
    }

    if (conflictingRanges.length === 0) {
      // Generate individual slots for the entire window when no conflicts
      const individualSlots = [];
      let slotStart = timeToMinutes(startTime);
      const rangeEnd = timeToMinutes(endTime);

      while (slotStart + serviceDuration <= rangeEnd) {
        const slotEndMinutes = slotStart + serviceDuration;
        individualSlots.push({
          start: minutesToTime(slotStart),
          end: minutesToTime(slotEndMinutes),
          isFree: true,
        });
        slotStart = slotEndMinutes;
      }

      return {
        freeRanges: [{ start: startTime, end: endTime }],
        totalFreeMinutes: timeToMinutes(endTime) - timeToMinutes(startTime),
        conflictedRanges: [],
        individualSlots,
        conflictedSlots: [],
        totalSlots: individualSlots.length,
      };
    }

    // Sort conflicting ranges by start time
    conflictingRanges.sort((a, b) => a.start.localeCompare(b.start));

    // Merge overlapping ranges
    const mergedConflicts = [];
    for (const range of conflictingRanges) {
      if (mergedConflicts.length === 0) {
        mergedConflicts.push({ ...range });
      } else {
        const last = mergedConflicts[mergedConflicts.length - 1];
        if (range.start <= last.end) {
          // Overlapping, extend the end
          last.end = range.end > last.end ? range.end : last.end;
        } else {
          mergedConflicts.push({ ...range });
        }
      }
    }

    // Calculate free ranges (gaps between conflicts)
    const freeRanges = [];
    let currentStart = startTime;

    for (const conflict of mergedConflicts) {
      // Only consider conflicts that overlap with our window
      if (conflict.end <= startTime || conflict.start >= endTime) continue;

      const conflictStart =
        conflict.start < startTime ? startTime : conflict.start;
      const conflictEnd = conflict.end > endTime ? endTime : conflict.end;

      // Free range before this conflict
      if (currentStart < conflictStart) {
        freeRanges.push({ start: currentStart, end: conflictStart });
      }
      currentStart = conflictEnd > currentStart ? conflictEnd : currentStart;
    }

    // Free range after last conflict
    if (currentStart < endTime) {
      freeRanges.push({ start: currentStart, end: endTime });
    }

    // Calculate total free minutes
    const totalFreeMinutes = freeRanges.reduce((sum, range) => {
      return sum + (timeToMinutes(range.end) - timeToMinutes(range.start));
    }, 0);

    // Filter conflicts that actually overlap with our window
    const conflictedRanges = mergedConflicts
      .filter((c) => !(c.end <= startTime || c.start >= endTime))
      .map((c) => ({
        start: c.start < startTime ? startTime : c.start,
        end: c.end > endTime ? endTime : c.end,
      }));

    // Generate ALL possible slots aligned to original window boundaries
    // Then categorize as free or conflicted based on overlap with conflicts
    // Slots are spaced by (serviceDuration + restTime) but each slot is only serviceDuration long
    const windowStartMinutes = timeToMinutes(startTime);
    const windowEndMinutes = timeToMinutes(endTime);

    const individualSlots = [];
    const conflictedSlots = [];

    let slotStart = windowStartMinutes;
    while (slotStart + serviceDuration <= windowEndMinutes) {
      const slotEnd = slotStart + serviceDuration;
      const slotStartTime = minutesToTime(slotStart);
      const slotEndTime = minutesToTime(slotEnd);

      // Check if this slot overlaps with any conflict
      const isConflicted = mergedConflicts.some((conflict) => {
        const conflictStart = timeToMinutes(conflict.start);
        const conflictEnd = timeToMinutes(conflict.end);
        // Overlap: slot starts before conflict ends AND slot ends after conflict starts
        return slotStart < conflictEnd && slotEnd > conflictStart;
      });

      if (isConflicted) {
        conflictedSlots.push({
          start: slotStartTime,
          end: slotEndTime,
          isFree: false,
        });
      } else {
        individualSlots.push({
          start: slotStartTime,
          end: slotEndTime,
          isFree: true,
        });
      }

      // Move to next slot with spacing (session + rest time)
      slotStart += slotSpacing;
    }

    return {
      freeRanges,
      totalFreeMinutes,
      conflictedRanges,
      individualSlots,
      conflictedSlots,
      totalSlots: individualSlots.length + conflictedSlots.length,
    };
  };

  // Helper to convert time string to minutes
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper to format minutes as time
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Sync calendar events with backend
  const syncCalendarWithBackend = async (events, fileName = null) => {
    try {
      // Convert events to blocked time slots format for backend
      const blockedSlots = events.map((event) => ({
        id: event.id,
        summary: event.summary,
        start:
          event.start instanceof Date ? event.start.toISOString() : event.start,
        end: event.end instanceof Date ? event.end.toISOString() : event.end,
        is_all_day: event.isAllDay || false,
      }));

      const response = await fetch(
        `${API_BASE_URL}/api/coach/calendar-blocks?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            blocked_slots: blockedSlots,
            calendar_file_name: fileName,
            imported_at: new Date().toISOString(),
          }),
        }
      );

      if (!response.ok) {
        console.warn('Failed to sync calendar with backend:', response.status);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error syncing calendar with backend:', error);
      return false;
    }
  };

  // Handle iCalendar file upload
  const handleCalendarFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.ics') && !file.name.endsWith('.ical')) {
      toastdisplay(
        'Please select an iCalendar file (.ics or .ical)',
        false,
        4000,
        setToast
      );
      return;
    }

    setCalendarSyncLoading(true);

    try {
      const content = await file.text();
      const events = parseICalendar(content);

      // Filter to keep only future events
      const now = new Date();
      const futureEvents = events.filter((event) => new Date(event.end) >= now);

      setImportedCalendarEvents(futureEvents);
      setCalendarFileName(file.name);

      // Save to localStorage for persistence
      localStorage.setItem(
        `coach_calendar_${user.email}`,
        JSON.stringify({
          fileName: file.name,
          events: futureEvents,
          importedAt: new Date().toISOString(),
        })
      );

      // Sync with backend for booking filtering
      const syncSuccess = await syncCalendarWithBackend(
        futureEvents,
        file.name
      );

      if (syncSuccess) {
        toastdisplay(
          `Calendar imported and synced! ${futureEvents.length} events loaded.`,
          true,
          4000,
          setToast
        );
      } else {
        toastdisplay(
          `Calendar imported locally (${futureEvents.length} events). Backend sync will be retried.`,
          true,
          4000,
          setToast
        );
      }
    } catch (error) {
      console.error('Error parsing calendar file:', error);
      toastdisplay(
        'Error reading calendar file. Please check the format.',
        false,
        4000,
        setToast
      );
    } finally {
      setCalendarSyncLoading(false);
      // Reset file input
      if (calendarInputRef.current) {
        calendarInputRef.current.value = '';
      }
    }
  };

  // Clear imported calendar
  const clearImportedCalendar = async () => {
    setCalendarSyncLoading(true);

    try {
      // Clear from backend
      await fetch(
        `${API_BASE_URL}/api/coach/calendar-blocks?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        }
      );

      // Clear local state
      setImportedCalendarEvents([]);
      setCalendarFileName('');
      localStorage.removeItem(`coach_calendar_${user.email}`);

      toastdisplay('Calendar removed', true, 2000, setToast);
    } catch (error) {
      console.error('Error clearing calendar:', error);
      // Still clear local state even if backend fails
      setImportedCalendarEvents([]);
      setCalendarFileName('');
      localStorage.removeItem(`coach_calendar_${user.email}`);

      toastdisplay('Calendar removed locally', true, 2000, setToast);
    } finally {
      setCalendarSyncLoading(false);
    }
  };

  // Load saved calendar from localStorage on mount and sync with backend
  useEffect(() => {
    const loadAndSyncCalendar = async () => {
      if (user?.email) {
        const savedCalendar = localStorage.getItem(
          `coach_calendar_${user.email}`
        );
        if (savedCalendar) {
          try {
            const parsed = JSON.parse(savedCalendar);
            // Filter out past events
            const now = new Date();
            const futureEvents = parsed.events.filter(
              (event) => new Date(event.end) >= now
            );
            setImportedCalendarEvents(futureEvents);
            setCalendarFileName(parsed.fileName);

            // Sync with backend to ensure consistency
            // This handles cases where the backend was unavailable during initial upload
            if (futureEvents.length > 0) {
              await syncCalendarWithBackend(futureEvents, parsed.fileName);
            }
          } catch (error) {
            console.error('Error loading saved calendar:', error);
          }
        }
      }
    };

    loadAndSyncCalendar();
  }, [user?.email]);

  const loadGoogleCalendarStatus = async () => {
    if (!user?.email) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/coach/google-calendar/status?user_email=${encodeURIComponent(user.email)}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) {
        setGcalStatus({ configured: false, connected: false });
        return;
      }
      setGcalStatus(await response.json());
    } catch (error) {
      console.error('Error loading Google Calendar status:', error);
    }
  };

  useEffect(() => {
    loadGoogleCalendarStatus();
  }, [user?.email]);

  useEffect(() => {
    const flag = searchParams.get('gcal');
    if (!flag) return;
    const messages = {
      connected: ['Google Calendar connected. We only read busy times.', true],
      denied: ['Google Calendar access was denied.', false],
      invalid_state: ['Google Calendar connection expired. Try again.', false],
      not_configured: ['Google Calendar is not configured on this server.', false],
      error: ['Could not connect Google Calendar.', false],
    };
    const entry = messages[flag] || [
      `Google Calendar: ${flag}`,
      flag === 'connected',
    ];
    toastdisplay(entry[0], entry[1], 4000, setToast);
    if (flag === 'connected') {
      loadGoogleCalendarStatus();
    }
    const next = new URLSearchParams(searchParams);
    next.delete('gcal');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const connectGoogleCalendar = async () => {
    try {
      setGcalBusy(true);
      const response = await fetch(
        `${API_BASE_URL}/api/coach/google-calendar/connect?user_email=${encodeURIComponent(user.email)}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Google Calendar is not configured');
      }
      if (data.auth_url) {
        window.location.assign(data.auth_url);
        return;
      }
      throw new Error('Missing Google authorization URL');
    } catch (error) {
      toastdisplay(
        error.message || 'Could not start Google Calendar connect',
        false,
        4000,
        setToast
      );
    } finally {
      setGcalBusy(false);
    }
  };

  const syncGoogleCalendar = async () => {
    try {
      setGcalBusy(true);
      const response = await fetch(
        `${API_BASE_URL}/api/coach/google-calendar/sync?user_email=${encodeURIComponent(user.email)}`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Sync failed');
      }
      toastdisplay(
        data.message || 'Google Calendar synced',
        true,
        3000,
        setToast
      );
      await loadGoogleCalendarStatus();
    } catch (error) {
      toastdisplay(
        error.message || 'Could not sync Google Calendar',
        false,
        4000,
        setToast
      );
    } finally {
      setGcalBusy(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      setGcalBusy(true);
      const response = await fetch(
        `${API_BASE_URL}/api/coach/google-calendar?user_email=${encodeURIComponent(user.email)}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      if (!response.ok) {
        throw new Error('Disconnect failed');
      }
      toastdisplay('Google Calendar disconnected', true, 2500, setToast);
      await loadGoogleCalendarStatus();
    } catch (error) {
      toastdisplay('Could not disconnect Google Calendar', false, 3000, setToast);
    } finally {
      setGcalBusy(false);
    }
  };

  useEffect(() => {
    if (!availableSchools || availableSchools.length === 0) {
      // Prevent duplicate school loading
      if (isLoadingSchoolsRef.current) {
        //console.log('Schools already loading, preventing duplicate fetch');
        return;
      }

      // Fetch from backend if not in localStorage
      const fetchSchools = async () => {
        isLoadingSchoolsRef.current = true;
        try {
          console.log('Fetching schools from backend...');
          const res = await fetch(`${API_BASE_URL}/api/tags`);
          if (!res.ok) throw new Error('Failed to load tags');
          const data = await res.json();
          if (Array.isArray(data)) {
            localStorage.setItem('schools', JSON.stringify(data));
            setAvailableSchools(data);
          } else {
            localStorage.setItem('schools', JSON.stringify([]));
            setAvailableSchools([]);
          }
        } catch (err) {
          console.error('Error fetching tags:', err);
          localStorage.setItem('schools', JSON.stringify([]));
          setAvailableSchools([]);
        } finally {
          isLoadingSchoolsRef.current = false;
        }
      };
      fetchSchools();
    }
  }, []);

  useEffect(() => {
    loadCoachData();
  }, []);

  const loadCoachData = async () => {
    // Prevent duplicate coach data loading
    if (isLoadingCoachDataRef.current) {
      //console.log('Coach data already loading, preventing duplicate fetch');
      return;
    }

    try {
      isLoadingCoachDataRef.current = true;
      setLoading(true);

      //console.log('Loading coach data from consolidated dashboard endpoint...');
      // Load all coach data from the consolidated dashboard endpoint
      await loadDashboardData();
      await Promise.all([
        loadEarnings(),
        loadOnboarding(),
        loadUpcomingBookings('7d'),
        loadRelectureCount(),
      ]);
    } catch (error) {
      console.error('Error loading coach data:', error);
      toastdisplay('Failed to load coach data', false, 3000, setToast);
    } finally {
      setLoading(false);
      isLoadingCoachDataRef.current = false;
    }
  };

  // Note: loadCoachProfile function removed - now using consolidated dashboard endpoint

  const loadReferrals = async () => {
    if (!user?.email) return;

    // Prevent duplicate referrals loading
    if (isLoadingReferralsRef.current) {
      //console.log('Referrals already loading, preventing duplicate fetch');
      return;
    }

    try {
      isLoadingReferralsRef.current = true;
      setReferralsLoading(true);

      //console.log('Fetching referrals from backend...');
      const response = await fetch(
        `${API_BASE_URL}/api/coach/referrals?user_email=${encodeURIComponent(user.email)}`,
        { headers: getAuthHeaders() }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setReferrals(data.referrals || []);
        } else {
          setReferrals([]);
        }
      } else {
        console.error('Failed to load referrals:', response.status);
        setReferrals([]);
      }
    } catch (error) {
      console.error('Error loading referrals:', error);
      setReferrals([]);
    } finally {
      setReferralsLoading(false);
      isLoadingReferralsRef.current = false;
    }
  };

  const loadEarnings = async (period = 'thisMonth') => {
    if (!user?.email) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/coach/earnings?user_email=${encodeURIComponent(user.email)}&period=${period}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) return;
      const data = await response.json();
      const summary = data.summary || {};
      setEarnings({
        totalRevenue: summary.total_revenue || 0,
        platformFee: summary.platform_fee || 0,
        vat: summary.vat || 0,
        netEarnings: summary.net_earnings || 0,
        pendingPayouts: summary.pending_payouts || 0,
        thisMonth: summary.this_month || 0,
        thisWeek: summary.this_week || 0,
        paidOut: summary.paid_out || 0,
        transactions: data.recent_earnings || [],
      });
    } catch (error) {
      console.error('Error loading earnings:', error);
    }
  };

  const loadOnboarding = async () => {
    if (!user?.email) return;
    try {
      const [statusRes, payoutRes] = await Promise.all([
        fetch(
          `${API_BASE_URL}/api/coach/onboarding-status?user_email=${encodeURIComponent(user.email)}`,
          { headers: getAuthHeaders() }
        ),
        fetch(
          `${API_BASE_URL}/api/coach/payout-method?user_email=${encodeURIComponent(user.email)}`,
          { headers: getAuthHeaders() }
        ),
      ]);
      if (statusRes.ok) {
        setOnboarding(await statusRes.json());
      }
      if (payoutRes.ok) {
        const payout = await payoutRes.json();
        setPayoutForm({
          payout_method: payout.payout_method || '',
          payout_details: payout.payout_details || '',
          review_sla_business_days: payout.review_sla_business_days || 3,
        });
      }
    } catch (error) {
      console.error('Error loading onboarding:', error);
    }
  };

  const agendaBounds = (range) => {
    const start = new Date();
    const end = new Date();
    if (range === '7d') {
      end.setDate(end.getDate() + 7);
    } else if (range === '30d') {
      end.setDate(end.getDate() + 30);
    } else {
      return {};
    }
    const iso = (d) => d.toISOString().slice(0, 10);
    return { from: iso(start), to: iso(end) };
  };

  const loadUpcomingBookings = async (range = agendaRange) => {
    if (!user?.email) return;
    try {
      const bounds = agendaBounds(range);
      const params = new URLSearchParams({
        user_email: user.email,
        upcoming: 'true',
      });
      if (bounds.from) params.set('from', bounds.from);
      if (bounds.to) params.set('to', bounds.to);
      const response = await fetch(
        `${API_BASE_URL}/api/coach/bookings?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) return;
      const rows = await response.json();
      setBookings(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error loading upcoming bookings:', error);
    }
  };

  const loadRelectureCount = async () => {
    if (!user?.is_document_reviewer && !coachProfile.isDocumentReviewer) {
      setRelectureCount(0);
      return;
    }
    try {
      const [applicationRes, fundingRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/coach/review-queue`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_BASE_URL}/api/coach/funding-queue`, {
          headers: getAuthHeaders(),
        }),
      ]);
      const applicationData = applicationRes.ok
        ? await applicationRes.json()
        : {};
      const fundingData = fundingRes.ok ? await fundingRes.json() : {};
      const count =
        (applicationData.reviews || []).length +
        (fundingData.reviews || []).length;
      setRelectureCount(count);
    } catch (error) {
      setRelectureCount(0);
    }
  };

  const loadDashboardData = async () => {
    // Prevent duplicate dashboard data loading
    if (isLoadingDashboardDataRef.current) {
      //console.log('Dashboard data already loading, preventing duplicate fetch');
      return;
    }

    try {
      isLoadingDashboardDataRef.current = true;

      //console.log('Fetching coach profile and dashboard data from backend...');

      // Fetch coach profile from backend (not localStorage)
      const profileResponse = await fetch(
        `${API_BASE_URL}/api/coach/${encodeURIComponent(user.email)}`,
        { headers: getAuthHeaders() }
      );

      if (!profileResponse.ok) {
        throw new Error('Failed to load coach profile');
      }

      const profileData = await profileResponse.json();
      //console.log('Coach profile data received:', profileData);

      // Map backend coach profile to frontend structure
      const mappedProfile = {
        id: profileData.id,
        user_id: profileData.user_id,
        name: `${profileData.first_name} ${profileData.last_name}`,
        firstName: profileData.first_name,
        lastName: profileData.last_name,
        email: profileData.email,
        profileImage: profileData.profile_picture || '',
        tagline: profileData.tagline || '',
        about: profileData.about || '',
        whyCoach: profileData.why_coach || '',
        linkedin: profileData.linkedin_url || '',
        website: profileData.website_url || '',
        phone: profileData.phone_number || '',
        timezone: profileData.timezone || 'UTC',
        responseTime: profileData.response_time || '1 hour',
        minBookingNotice: profileData.min_booking_notice || 1, // Buffer time in days
        isActive: profileData.is_active || false,
        isVerified: profileData.is_verified || false,
        isDocumentReviewer: Boolean(profileData.is_document_reviewer),
        totalSessions: profileData.total_sessions || 0,
        totalEarnings: profileData.total_earnings || 0,
        workExperience: profileData.work_experiences || [],
        education: profileData.educations || [],
        specializedSchools: profileData.specialized_schools || [],
        sectorsOfInterest: profileData.sectors_of_interest || [],
        expertises: profileData.expertises || [],
        lookingToMeet: profileData.looking_to_meet || [],
        avgRating: profileData.average_rating || 0,
        totalReviews: (profileData.reviews || []).length,
        createdAt: profileData.created_at,
        updatedAt: profileData.updated_at,
      };

      setCoachProfile(mappedProfile);

      // Cache to localStorage after fetching from backend
      saveCoachToLocalStorage(user.email, mappedProfile);

      // Handle services from profile endpoint
      if (profileData.services) {
        const mappedServices = profileData.services.map((service) => ({
          id: service.id,
          title: service.title,
          description: service.description,
          duration: service.duration,
          restTime: service.rest_time || 0, // Buffer time between meetings
          price: service.price,
          priceUnit: service.price_unit || 'per session',
          service_type: service.service_type,
          type: service.service_type,
          is_active: true, // Only active services returned
          isFree: service.is_free,
          maxParticipants: service.max_participants || 1,
          bookings_count: service.bookings_count || 0,
          bookingsCount: service.bookings_count || 0,
          total_bookings: service.bookings_count || 0,
          // Parse simplified availability structure from backend
          ...parseAvailabilityFromBackend(service.availability),
          excludedSlots: service.excluded_slots || {},
          stripe_payment_link: service.payment_link || null,
          product_id: service.product_id || null,
        }));
        setServices(mappedServices);
      }

      // Handle reviews from profile endpoint
      if (profileData.reviews) {
        const mappedReviews = profileData.reviews.map((review) => ({
          id: review.id,
          rating: review.rating,
          comment: review.comment,
          studentName: review.student_name,
          studentEmail: review.student_email,
          serviceTitle: review.service_title,
          date: review.date,
          verified: review.is_verified,
          published: review.is_published,
        }));
        setReviews(mappedReviews);
      }

      // Now fetch dashboard stats
      const dashboardResponse = await fetch(
        `${API_BASE_URL}/api/coach/dashboard?user_email=${encodeURIComponent(user.email)}`,
        { headers: getAuthHeaders() }
      );

      if (dashboardResponse.ok) {
        const dashboardResponse_json = await dashboardResponse.json();
        // Backend wraps response in { success: true, data: {...} }
        const dashboardData =
          dashboardResponse_json.data || dashboardResponse_json;
        //console.log('Dashboard stats received:', dashboardData);

        // Handle stats data
        if (dashboardData.stats) {
          const stats = dashboardData.stats;
          setEarnings((prev) => ({
            ...prev,
            thisMonth: stats.this_month_earnings || prev.thisMonth || 0,
          }));
        }

        // Services and reviews already loaded from profile endpoint
        // Handle bookings data - use recent_bookings from dashboard
        if (dashboardData.recent_bookings) {
          // Map bookings to ensure compatibility with existing display logic
          const mappedBookings = dashboardData.recent_bookings.map(
            (booking) => {
              const duration = booking.duration || 60; // Default duration

              return {
                id: booking.id,
                service_id: booking.service_id,
                coach_profile_id: booking.coach_profile_id,
                student_id: booking.student_id,
                service_title: booking.service_title,
                student_name: booking.student_name,
                student_email: booking.student_email,
                booking_date: booking.booking_date,
                start_time: booking.start_time,
                end_time: booking.end_time,
                status: booking.status,
                price_paid: booking.price_paid,
                duration: duration,
                student_message: booking.student_message || null,
                meeting_link: booking.meeting_link || null,
                coach_notes: booking.coach_notes || null,
                created_at: booking.created_at,
              };
            }
          );
          setBookings(mappedBookings);
        }

        // Reviews already loaded from profile endpoint

        // Load referrals separately as they're not in the dashboard response
        if (!isLoadingReferralsRef.current) {
          loadReferrals();
        }
      } else {
        console.error(
          'Failed to load dashboard data:',
          dashboardResponse.status
        );
        throw new Error('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set empty data as fallback
      setBookings([]);
      setServices([]);
      setReviews([]);
      setEarnings({
        totalRevenue: 0,
        platformFee: 0,
        vat: 0,
        netEarnings: 0,
        pendingPayouts: 0,
        thisMonth: 0,
        thisWeek: 0,
        paidOut: 0,
        transactions: [],
      });
    } finally {
      isLoadingDashboardDataRef.current = false;
    }
  };

  const SERVICE_DAYS = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  const SERVICE_DAY_SHORT = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
  };
  const SERVICE_DAY_LABEL = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
  };

  const emptyAvailability = () =>
    SERVICE_DAYS.reduce((acc, day) => {
      acc[day] = [];
      return acc;
    }, {});

  const defaultNewService = () => ({
    title: '',
    description: '',
    duration: 60,
    restTime: 0,
    price: '',
    priceUnit: 'per session',
    type: 'individual',
    isFree: false,
    maxParticipants: 1,
    is_active: true,
    availability: emptyAvailability(),
    weeklyOverrides: {},
    excludedSlots: {},
  });

  const shouldUseFullscreenEditor = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 1024px)').matches;

  const openServiceEditor = (service = null) => {
    setServiceModalStep('details');
    setShowAvailabilityAdvanced(false);
    setShowServiceMoreDetails(false);
    setServiceModalFullscreen(shouldUseFullscreenEditor());
    setSelectedCopyDay(null);
    setSelectedWeekOffset(0);
    setQuickHours({ start: '09:00', end: '17:00' });
    if (service) {
      setEditingService(service);
      setNewService({
        ...service,
        restTime: service.restTime || 0,
        availability: service.availability || emptyAvailability(),
        weeklyOverrides: service.weeklyOverrides || {},
        excludedSlots: service.excludedSlots || {},
      });
    } else {
      setEditingService(null);
      setNewService(defaultNewService());
    }
    setShowServiceModal(true);
  };

  const closeServiceEditor = () => {
    setShowServiceModal(false);
    setSelectedCopyDay(null);
    setSelectedWeekOffset(0);
    setServiceModalFullscreen(false);
    setServiceModalStep('details');
    setShowAvailabilityAdvanced(false);
    setShowServiceMoreDetails(false);
  };

  const formatHourSlots = (slots) =>
    (slots || [])
      .map((slot) => {
        const start = slot.start || slot.start_time;
        const end = slot.end || slot.end_time;
        return start && end ? `${start}–${end}` : '';
      })
      .filter(Boolean)
      .join(', ');

  const compactServiceHours = (service) => {
    const availability = service?.availability || {};
    const groups = {};
    SERVICE_DAYS.forEach((day) => {
      const slots = availability[day];
      if (!Array.isArray(slots) || slots.length === 0) return;
      const text = formatHourSlots(slots);
      if (!text) return;
      groups[text] = groups[text] || [];
      groups[text].push(SERVICE_DAY_SHORT[day]);
    });
    const lines = Object.entries(groups).map(
      ([text, labels]) => `${labels.join(', ')} ${text}`
    );
    const overrideCount = Object.keys(
      service?.weeklyOverrides || service?.weekly_overrides || {}
    ).length;
    if (overrideCount > 0) {
      lines.push(
        `${overrideCount} custom week${overrideCount > 1 ? 's' : ''}`
      );
    }
    return lines;
  };

  const applyQuickHoursToDays = (days, start, end) => {
    if (!start || !end || start >= end) {
      toastdisplay('End time must be after start time', false, 3000, setToast);
      return;
    }
    setSelectedWeekOffset(0);
    setNewService((prev) => {
      const availability = { ...prev.availability };
      days.forEach((day) => {
        availability[day] = [{ id: Date.now() + Math.random(), start, end }];
      });
      return { ...prev, availability };
    });
  };

  const toggleSimpleDay = (day) => {
    const start = quickHours.start || '09:00';
    const end = quickHours.end || '17:00';
    setSelectedWeekOffset(0);
    setNewService((prev) => {
      const currentSlots = prev.availability?.[day] || [];
      return {
        ...prev,
        availability: {
          ...prev.availability,
          [day]:
            currentSlots.length > 0
              ? []
              : [{ id: Date.now(), start, end }],
        },
      };
    });
  };

  const removeSimpleDaySlot = (day, slotId) => {
    setSelectedWeekOffset(0);
    setNewService((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: (prev.availability?.[day] || []).filter(
          (slot) => slot.id !== slotId
        ),
      },
    }));
  };

  // Service validation functions
  const isServiceDetailsValid = () => {
    if (!newService.title.trim()) return false;
    if (!newService.description.trim()) return false;
    if (!newService.duration || newService.duration < 5) return false;
    if (!newService.isFree) {
      if (!newService.price || parseFloat(newService.price) <= 0) return false;
    }
    return true;
  };

  const isServiceValid = () => isServiceDetailsValid();

  const getServiceValidationErrors = () => {
    const errors = [];

    if (!newService.title.trim()) {
      errors.push('Service title is required');
    }
    if (!newService.description.trim()) {
      errors.push('Service description is required');
    }
    if (!newService.duration || newService.duration < 5) {
      errors.push('Duration must be at least 5 minutes');
    }
    if (
      !newService.isFree &&
      (!newService.price || parseFloat(newService.price) <= 0)
    ) {
      errors.push('Price must be greater than 0 for paid services');
    }

    const hasTimeSlots = Object.values(newService.availability).some(
      (daySlots) => daySlots.length > 0
    );
    if (!hasTimeSlots) {
      errors.push('At least one time slot must be set for any day of the week');
    }

    return errors;
  };

  // Service save function
  const handleServiceSave = async () => {
    const errors = getServiceValidationErrors();
    if (errors.length) {
      const hoursMissing = errors.some((error) =>
        error.toLowerCase().includes('time slot')
      );
      setServiceModalStep(hoursMissing ? 'hours' : 'details');
      toastdisplay(
        `Please fix the following issues:\n• ${errors.join('\n• ')}`,
        false,
        5000,
        setToast
      );
      return;
    }

    try {
      setLoading(true);

      // Prepare service data according to backend API spec
      // Build availability with individual timeslots based on service duration + rest time
      const buildAvailabilityPayload = () => {
        const availability = {};
        const serviceDuration = newService.duration || 30;
        const restTime = newService.restTime || 0;
        // Total slot spacing = session duration + rest time between sessions
        const slotSpacing = serviceDuration + restTime;

        // Helper to generate individual slots from a time window
        const generateIndividualSlots = (window, day) => {
          const slots = [];
          const startMinutes = timeToMinutes(window.start);
          const endMinutes = timeToMinutes(window.end);

          let currentStart = startMinutes;
          // Each slot takes up slotSpacing minutes (session + rest), but ends at session duration
          while (currentStart + serviceDuration <= endMinutes) {
            const slotStart = minutesToTime(currentStart);
            const slotEnd = minutesToTime(currentStart + serviceDuration);
            const slotKey = `${slotStart}-${slotEnd}`;

            // Only include slot if not excluded
            const isExcluded = (newService.excludedSlots[day] || []).includes(
              slotKey
            );
            if (!isExcluded) {
              slots.push({ start: slotStart, end: slotEnd });
            }
            // Move to next slot: skip the session duration + rest time
            currentStart += slotSpacing;
          }
          return slots;
        };

        // Helper to process availability for a day
        const processDay = (dayWindows, day) => {
          if (!dayWindows || dayWindows.length === 0) return [];
          const allSlots = [];
          dayWindows.forEach((window) => {
            const slots = generateIndividualSlots(window, day);
            allSlots.push(...slots);
          });
          return allSlots;
        };

        const days = [
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
          'sunday',
        ];

        // Add recurring schedule with individual slots
        availability.recurring = {};
        days.forEach((day) => {
          availability.recurring[day] = processDay(
            newService.availability[day],
            day
          );
        });

        // Add week-specific overrides with individual slots
        if (newService.weeklyOverrides) {
          Object.entries(newService.weeklyOverrides).forEach(
            ([weekOffset, weekData]) => {
              // Check if this week actually has any slots configured
              const hasAnySlots =
                weekData &&
                Object.values(weekData).some(
                  (daySlots) => Array.isArray(daySlots) && daySlots.length > 0
                );

              if (hasAnySlots) {
                availability[`week_${weekOffset}`] = {};
                days.forEach((day) => {
                  availability[`week_${weekOffset}`][day] = processDay(
                    weekData[day],
                    day
                  );
                });
              }
            }
          );
        }

        return availability;
      };

      // Remove slots from availability that conflict with other active services.
      // Week-aware rule:
      // - For `recurring`, only compare against other services' recurring availability
      // - For `week_X`, only compare against other services' `week_X` availability
      const removeServiceConflictsFromAvailability = (availabilityPayload) => {
        if (!availabilityPayload) return availabilityPayload;

        const otherActiveServices = services.filter(
          (service) =>
            service.is_active &&
            (!editingService || service.id !== editingService.id)
        );

        if (otherActiveServices.length === 0) return availabilityPayload;

        const normalizeSlots = (slots) => {
          if (!Array.isArray(slots)) return [];
          return slots
            .map((slot) => ({
              start: slot?.start || slot?.start_time,
              end: slot?.end || slot?.end_time,
            }))
            .filter((s) => s.start && s.end);
        };

        const overlapsAny = (slotStart, slotEnd, ranges) => {
          const startM = timeToMinutes(slotStart);
          const endM = timeToMinutes(slotEnd);
          return ranges.some(({ start, end }) => {
            const rStart = timeToMinutes(start);
            const rEnd = timeToMinutes(end);
            // overlap if not disjoint
            return !(endM <= rStart || startM >= rEnd);
          });
        };

        const getOtherServiceSlots = ({ service, day, scope }) => {
          const dayLower = (day || '').toLowerCase();
          const availability = service?.availability;
          const weeklyOverrides =
            service?.weeklyOverrides || service?.weekly_overrides || {};

          // scope: { type: 'recurring' } | { type: 'week', weekNumber: number }
          if (scope?.type === 'recurring') {
            // recurring only (do NOT fall back to week_X)
            if (availability?.recurring?.[dayLower]) {
              return normalizeSlots(availability.recurring[dayLower]);
            }
            if (Array.isArray(availability?.[dayLower])) {
              return normalizeSlots(availability[dayLower]);
            }
            return [];
          }

          if (scope?.type === 'week') {
            const weekNumber = scope.weekNumber;
            // week-specific only (do NOT fall back to recurring)
            if (
              weeklyOverrides?.[weekNumber] &&
              Array.isArray(weeklyOverrides[weekNumber]?.[dayLower])
            ) {
              return normalizeSlots(weeklyOverrides[weekNumber][dayLower]);
            }
            const weekKey = `week_${weekNumber}`;
            if (availability?.[weekKey] && Array.isArray(availability[weekKey]?.[dayLower])) {
              return normalizeSlots(availability[weekKey][dayLower]);
            }
            return [];
          }

          return [];
        };

        const cleaned = { ...availabilityPayload };

        // 1) Clean recurring slots against other services recurring
        if (cleaned.recurring && typeof cleaned.recurring === 'object') {
          const newRecurring = { ...cleaned.recurring };
          Object.keys(newRecurring).forEach((day) => {
            const daySlots = normalizeSlots(newRecurring[day]);
            if (daySlots.length === 0) return;

            const conflictRanges = [];
            for (const service of otherActiveServices) {
              conflictRanges.push(
                ...getOtherServiceSlots({ service, day, scope: { type: 'recurring' } })
              );
            }

            if (conflictRanges.length === 0) return;

            newRecurring[day] = daySlots.filter(
              (slot) => !overlapsAny(slot.start, slot.end, conflictRanges)
            );
          });
          cleaned.recurring = newRecurring;
        }

        // 2) Clean week_X slots against other services week_X
        Object.keys(cleaned).forEach((key) => {
          if (!key.startsWith('week_')) return;
          const weekNumber = parseInt(key.replace('week_', ''), 10);
          if (!Number.isFinite(weekNumber)) return;

          const weekObj = cleaned[key];
          if (!weekObj || typeof weekObj !== 'object') return;

          const newWeekObj = { ...weekObj };
          Object.keys(newWeekObj).forEach((day) => {
            const daySlots = normalizeSlots(newWeekObj[day]);
            if (daySlots.length === 0) return;

            const conflictRanges = [];
            for (const service of otherActiveServices) {
              conflictRanges.push(
                ...getOtherServiceSlots({
                  service,
                  day,
                  scope: { type: 'week', weekNumber },
                })
              );
            }

            if (conflictRanges.length === 0) return;

            newWeekObj[day] = daySlots.filter(
              (slot) => !overlapsAny(slot.start, slot.end, conflictRanges)
            );
          });
          cleaned[key] = newWeekObj;
        });

        return cleaned;
      };

      // Build excluded_slots from calendar conflicts + other service conflicts
      // Note: Manual slot toggles (day-based like "monday": ["12:00-12:30"]) are handled
      // by NOT including them in availability, not by adding them to excluded_slots
      const buildExcludedSlotsPayload = () => {
        // Start fresh - only include date-based exclusions (YYYY-MM-DD format)
        // Filter out any day-name keys that might be in the state
        const excludedSlots = {};
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

        // Copy only date-based exclusions from existing state
        Object.entries(newService.excludedSlots || {}).forEach(
          ([key, value]) => {
            if (dateRegex.test(key)) {
              excludedSlots[key] = value;
            }
          }
        );

        // Helper to add a slot to excludedSlots without duplicates
        const addExcludedSlot = (dateStr, start, end, reason) => {
          if (!excludedSlots[dateStr]) {
            excludedSlots[dateStr] = [];
          }
          const slotExists = excludedSlots[dateStr].some(
            (slot) => slot.start === start && slot.end === end
          );
          if (!slotExists) {
            excludedSlots[dateStr].push({ start, end, reason });
          }
        };

        // 1. Add calendar-imported events as excluded slots
        if (importedCalendarEvents && importedCalendarEvents.length > 0) {
          importedCalendarEvents.forEach((event) => {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);

            // Format date as YYYY-MM-DD
            const dateStr = eventStart.toISOString().split('T')[0];

            // Format times as HH:MM
            const startTime = eventStart.toTimeString().slice(0, 5);
            const endTime = eventEnd.toTimeString().slice(0, 5);

            addExcludedSlot(
              dateStr,
              startTime,
              endTime,
              event.summary || event.title || 'Calendar event'
            );
          });
        }

        // 2. Add conflicts from other active services
        const otherActiveServices = services.filter(
          (service) =>
            service.is_active &&
            (!editingService || service.id !== editingService.id)
        );

        if (otherActiveServices.length > 0) {
          const daysOfWeek = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
          ];

          // Check 8 weeks ahead for conflicts (56 days)
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          for (let daysAhead = 0; daysAhead < 56; daysAhead++) {
            const checkDate = new Date(today);
            checkDate.setDate(today.getDate() + daysAhead);

            const dateStr = checkDate.toISOString().split('T')[0];
            const dayName = daysOfWeek[checkDate.getDay()];

            // Calculate which week this date falls into (1-indexed)
            // Week 1 = days 0-6 from today, Week 2 = days 7-13, etc.
            const weekNumber = Math.floor(daysAhead / 7) + 1;

            // Check each other service for conflicts on this day
            for (const service of otherActiveServices) {
              const availability = service.availability;
              const weeklyOverrides =
                service.weeklyOverrides || service.weekly_overrides || {};

              // Get slots for this day from the service
              // Priority: week-specific > recurring
              let daySlots = [];

              // First, check for week-specific override (highest priority)
              // Check parsed weeklyOverrides format
              if (
                weeklyOverrides[weekNumber] &&
                weeklyOverrides[weekNumber][dayName] &&
                Array.isArray(weeklyOverrides[weekNumber][dayName])
              ) {
                daySlots = weeklyOverrides[weekNumber][dayName];
              }
              // Also check raw availability format (week_1, week_2, etc.)
              else {
                const weekKey = `week_${weekNumber}`;
                if (
                  availability &&
                  availability[weekKey] &&
                  availability[weekKey][dayName] &&
                  Array.isArray(availability[weekKey][dayName])
                ) {
                  daySlots = availability[weekKey][dayName];
                }
                // Fall back to recurring availability if no week-specific override
                else if (availability) {
                  // Check if availability is in day-based format (parsed recurring)
                  if (
                    availability[dayName] &&
                    Array.isArray(availability[dayName])
                  ) {
                    daySlots = availability[dayName];
                  }
                  // Check if availability is in recurring format
                  else if (
                    availability.recurring &&
                    availability.recurring[dayName]
                  ) {
                    daySlots = availability.recurring[dayName];
                  }
                }
              }

              // Add each slot as excluded
              daySlots.forEach((slot) => {
                const slotStart = slot.start || slot.start_time;
                const slotEnd = slot.end || slot.end_time;
                if (slotStart && slotEnd) {
                  addExcludedSlot(
                    dateStr,
                    slotStart,
                    slotEnd,
                    `Conflict with service: ${service.title}`
                  );
                }
              });
            }
          }
        }

        return excludedSlots;
      };

      const availabilityPayload = buildAvailabilityPayload();
      const cleanedAvailabilityPayload =
        removeServiceConflictsFromAvailability(availabilityPayload);

      const serviceData = {
        title: newService.title,
        description: newService.description,
        duration: parseInt(newService.duration) || 60,
        rest_time: parseInt(newService.restTime) || 0, // Buffer time between meetings
        price: newService.isFree ? 0 : parseFloat(newService.price) || 0,
        price_unit: newService.priceUnit,
        service_type: newService.type,
        is_free: newService.isFree,
        max_participants: parseInt(newService.maxParticipants) || 1,
        // Remove cross-service conflicts directly from availability before saving
        availability: cleanedAvailabilityPayload,
        // Excluded slots include manual exclusions + calendar-imported conflicts
        excluded_slots: buildExcludedSlotsPayload(),
      };

      let response;
      if (editingService) {
        // Update existing service
        response = await fetch(
          `${API_BASE_URL}/api/coach/services/${editingService.id}?user_email=${encodeURIComponent(user.email)}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(serviceData),
          }
        );
      } else {
        // Create new service
        response = await fetch(
          `${API_BASE_URL}/api/coach/services?user_email=${encodeURIComponent(user.email)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(serviceData),
          }
        );
      }

      if (response.ok) {
        const result = await response.json();

        if (editingService) {
          toastdisplay(
            'Service updated successfully! Refreshing...',
            true,
            2000,
            setToast
          );

          // Reload dashboard data to get fresh service data from backend
          await loadDashboardData();

          // Update localStorage with profile data only (services not cached)
          saveCoachToLocalStorage(user.email, coachProfile);
        } else {
          // Add new service to local state
          const newServiceWithId = {
            ...serviceData,
            id: result.data?.service_id || Date.now(),
            bookings_count: 0,
            total_bookings: 0,
            is_active: true, // Default new services to active
            stripe_payment_link: result.data?.stripe_payment_link || null,
          };
          const updatedServices = [...services, newServiceWithId];
          setServices(updatedServices);

          // Update localStorage with profile data only (services not cached)
          saveCoachToLocalStorage(user.email, coachProfile);

          // Show success message with payment link info
          const successMessage = result.data?.stripe_payment_link
            ? 'Service created successfully with payment link!'
            : 'Service created successfully!';
          toastdisplay(successMessage, true, 3000, setToast);
        }

        closeServiceEditor();
        setEditingService(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save service');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      toastdisplay(
        `Failed to save service: ${error.message}`,
        false,
        3000,
        setToast
      );
    } finally {
      setLoading(false);
    }
  };

  // Save profile function
  const handleProfileSave = async () => {
    // Validate profile before saving
    const validationErrors = getProfileValidationErrors();

    if (validationErrors.length > 0) {
      setProfileValidationErrors(validationErrors);

      // Create error message listing missing fields
      const missingFields = validationErrors.map((e) => e.message).join(', ');
      toastdisplay(
        `Please complete all required fields: ${missingFields}`,
        false,
        6000,
        setToast
      );

      // Scroll to the first error field
      const firstErrorField = validationErrors[0].field;
      const errorElement = document.querySelector(
        `[data-field="${firstErrorField}"]`
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }

    // Clear any previous validation errors
    setProfileValidationErrors([]);

    try {
      setLoading(true);

      // Prepare profile data according to backend API spec
      const profileData = {
        name: coachProfile.name,
        email: user.email,
        tagline: coachProfile.tagline,
        about: coachProfile.about,
        why_coach: coachProfile.whyCoach,
        linkedin_url: coachProfile.linkedin,
        website_url: coachProfile.website,
        phone_number: coachProfile.phone,
        timezone: coachProfile.timezone,
        response_time: coachProfile.responseTime,
        min_booking_notice: parseInt(coachProfile.minBookingNotice) || 1,
        work_experiences: coachProfile.workExperience.map((exp) => ({
          company: exp.company,
          title: exp.title,
          duration: exp.duration,
          description: exp.description || '',
          order_index: exp.order_index || 0,
        })),
        educations: coachProfile.education.map((edu) => ({
          school: edu.school,
          degree: edu.degree,
          year: edu.year,
          description: edu.description || '',
          order_index: edu.order_index || 0,
        })),
        specialized_schools: coachProfile.specializedSchools,
        sectors_of_interest: coachProfile.sectorsOfInterest.map(
          (sector) => sector.name
        ),
        expertises: coachProfile.expertises.map((exp) => exp.name),
        looking_to_meet: coachProfile.lookingToMeet.map((item) => item.name),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/coach-profile?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(profileData),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toastdisplay('Profile saved successfully!', true, 3000, setToast);

          // Update localStorage with profile data only (services not cached)
          saveCoachToLocalStorage(user.email, coachProfile);
        } else {
          throw new Error(result.message || 'Failed to save profile');
        }
      } else {
        const errorData = await response.json();
        // Check if it's a validation error from the backend
        if (errorData.detail && errorData.detail.includes('Object')) {
          // Re-validate and show specific errors
          const errors = getProfileValidationErrors();
          if (errors.length > 0) {
            setProfileValidationErrors(errors);
            const missingFields = errors.map((e) => e.message).join(', ');
            toastdisplay(
              `Some required fields are missing: ${missingFields}`,
              false,
              6000,
              setToast
            );
          } else {
            toastdisplay(
              'Failed to save profile. Please check all required fields are filled correctly.',
              false,
              5000,
              setToast
            );
          }
        } else {
          throw new Error(errorData.detail || 'Failed to save profile');
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      // Check if it's an Object-related error
      if (error.message && error.message.includes('Object')) {
        const errors = getProfileValidationErrors();
        if (errors.length > 0) {
          setProfileValidationErrors(errors);
          const missingFields = errors.map((e) => e.message).join(', ');
          toastdisplay(
            `Some required fields are missing: ${missingFields}`,
            false,
            6000,
            setToast
          );
        } else {
          toastdisplay(
            'Failed to save profile. Please ensure all fields are filled correctly.',
            false,
            5000,
            setToast
          );
        }
      } else {
        toastdisplay(
          `Failed to save profile: ${error.message}`,
          false,
          3000,
          setToast
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleServiceDelete = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/coach/services/${serviceId}?user_email=${encodeURIComponent(user.email)}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // Check if service was deactivated or permanently deleted
            const statusDeleted = result.status_deleted;

            if (statusDeleted === 'deactivated') {
              // Service was deactivated (has bookings) - update it in the list
              const updatedServices = services.map((s) =>
                s.id === serviceId ? { ...s, is_active: false } : s
              );
              setServices(updatedServices);

              toastdisplay(
                result.message ||
                  'Service deactivated. The service has existing bookings and has been deactivated instead of deleted. Students can no longer book this service, but booking history is preserved.',
                true,
                6000,
                setToast
              );
            } else {
              // Service was permanently deleted (no bookings) - remove from list
              const updatedServices = services.filter(
                (s) => s.id !== serviceId
              );
              setServices(updatedServices);

              toastdisplay(
                result.message || 'Service permanently deleted successfully!',
                true,
                3000,
                setToast
              );
            }

            // Update localStorage with profile data only (services not cached)
            saveCoachToLocalStorage(user.email, coachProfile);
          } else {
            throw new Error(result.message || 'Failed to delete service');
          }
        } else {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to delete service');
        }
      } catch (error) {
        console.error('Error deleting service:', error);
        toastdisplay(
          `Failed to delete service: ${error.message}`,
          false,
          3000,
          setToast
        );
      }
    }
  };

  // Work Experience handlers
  const addWorkExperience = () => {
    const newExp = {
      id: Date.now(),
      company: '',
      title: '',
      duration: '',
      description: '',
    };
    setCoachProfile({
      ...coachProfile,
      workExperience: [...coachProfile.workExperience, newExp],
    });
  };

  const updateWorkExperience = (id, field, value) => {
    setCoachProfile({
      ...coachProfile,
      workExperience: coachProfile.workExperience.map((exp) =>
        exp.id === id ? { ...exp, [field]: value } : exp
      ),
    });
  };

  const removeWorkExperience = (id) => {
    setCoachProfile({
      ...coachProfile,
      workExperience: coachProfile.workExperience.filter(
        (exp) => exp.id !== id
      ),
    });
  };

  // Education handlers
  const addEducation = () => {
    const newEdu = {
      id: Date.now(),
      school: '',
      degree: '',
      year: '',
      description: '',
    };
    setCoachProfile({
      ...coachProfile,
      education: [...coachProfile.education, newEdu],
    });
  };

  const updateEducation = (id, field, value) => {
    setCoachProfile({
      ...coachProfile,
      education: coachProfile.education.map((edu) =>
        edu.id === id ? { ...edu, [field]: value } : edu
      ),
    });
  };

  const removeEducation = (id) => {
    setCoachProfile({
      ...coachProfile,
      education: coachProfile.education.filter((edu) => edu.id !== id),
    });
  };

  // School specialization handlers
  const toggleSchoolSpecialization = (schoolName) => {
    const isSelected = coachProfile.specializedSchools.includes(schoolName);
    if (isSelected) {
      setCoachProfile({
        ...coachProfile,
        specializedSchools: coachProfile.specializedSchools.filter(
          (s) => s !== schoolName
        ),
      });
    } else {
      setCoachProfile({
        ...coachProfile,
        specializedSchools: [...coachProfile.specializedSchools, schoolName],
      });
    }
  };

  // Sectors of Interest handlers
  const addSectorOfInterest = () => {
    const newSector = {
      id: Date.now(),
      name: '',
    };
    setCoachProfile({
      ...coachProfile,
      sectorsOfInterest: [...coachProfile.sectorsOfInterest, newSector],
    });
  };

  const updateSectorOfInterest = (id, value) => {
    setCoachProfile({
      ...coachProfile,
      sectorsOfInterest: coachProfile.sectorsOfInterest.map((sector) =>
        sector.id === id ? { ...sector, name: value } : sector
      ),
    });
  };

  const removeSectorOfInterest = (id) => {
    setCoachProfile({
      ...coachProfile,
      sectorsOfInterest: coachProfile.sectorsOfInterest.filter(
        (sector) => sector.id !== id
      ),
    });
  };

  // Expertises handlers
  const addExpertise = () => {
    const newExpertise = {
      id: Date.now(),
      name: '',
    };
    setCoachProfile({
      ...coachProfile,
      expertises: [...coachProfile.expertises, newExpertise],
    });
  };

  const updateExpertise = (id, value) => {
    setCoachProfile({
      ...coachProfile,
      expertises: coachProfile.expertises.map((expertise) =>
        expertise.id === id ? { ...expertise, name: value } : expertise
      ),
    });
  };

  const removeExpertise = (id) => {
    setCoachProfile({
      ...coachProfile,
      expertises: coachProfile.expertises.filter(
        (expertise) => expertise.id !== id
      ),
    });
  };

  // Looking to Meet handlers
  const addLookingToMeet = () => {
    const newLookingToMeet = {
      id: Date.now(),
      name: '',
    };
    setCoachProfile({
      ...coachProfile,
      lookingToMeet: [...coachProfile.lookingToMeet, newLookingToMeet],
    });
  };

  const updateLookingToMeet = (id, value) => {
    setCoachProfile({
      ...coachProfile,
      lookingToMeet: coachProfile.lookingToMeet.map((item) =>
        item.id === id ? { ...item, name: value } : item
      ),
    });
  };

  const removeLookingToMeet = (id) => {
    setCoachProfile({
      ...coachProfile,
      lookingToMeet: coachProfile.lookingToMeet.filter(
        (item) => item.id !== id
      ),
    });
  };

  // Helper function to parse simplified availability structure from backend
  // Backend returns: { "recurring": { "monday": [...], ... }, "week_1": {...}, ... }
  // Frontend needs: { availability: { monday: [...], ... }, weeklyOverrides: { 1: {...}, ... } }
  const parseAvailabilityFromBackend = (backendAvailability) => {
    const emptyWeek = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    };

    if (!backendAvailability) {
      return { availability: { ...emptyWeek }, weeklyOverrides: {} };
    }

    let idCounter = 0;
    const addIdsToSlots = (weekData) => {
      const result = {};
      Object.keys(emptyWeek).forEach((day) => {
        result[day] = (weekData?.[day] || []).map((slot) => ({
          id: Date.now() + idCounter++,
          start: slot.start,
          end: slot.end,
        }));
      });
      return result;
    };

    // Extract recurring schedule
    const recurringData = backendAvailability.recurring || backendAvailability;
    const availability = addIdsToSlots(recurringData);

    // Extract week-specific overrides (week_1, week_2, etc.)
    const weeklyOverrides = {};
    Object.keys(backendAvailability).forEach((key) => {
      const weekMatch = key.match(/^week_(\d+)$/);
      if (weekMatch) {
        const weekOffset = parseInt(weekMatch[1], 10);
        weeklyOverrides[weekOffset] = addIdsToSlots(backendAvailability[key]);
      }
    });

    return { availability, weeklyOverrides };
  };

  // Helper function to convert date-based availability to day-of-week format
  const convertAvailabilityToWeeklyFormat = (availability) => {
    const weeklyAvailability = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    };

    if (!availability) return weeklyAvailability;

    // Counter to ensure unique IDs across all slots
    let idCounter = 0;

    // Iterate through all keys in availability
    Object.keys(availability).forEach((key) => {
      const entry = availability[key];

      // Skip if it's already a day name (empty array) or not an object
      if (Array.isArray(entry) || !entry || typeof entry !== 'object') {
        return;
      }

      // Check if this is a date-based entry (has day_name and slots)
      if (entry.day_name && entry.slots && Array.isArray(entry.slots)) {
        const dayName = entry.day_name.toLowerCase();

        // Convert slots to the format expected by the UI
        const convertedSlots = entry.slots.map((slot) => {
          // Generate truly unique ID using timestamp + counter
          const uniqueId = Date.now() + idCounter++;

          return {
            id: uniqueId,
            start: slot.start,
            end: slot.end,
          };
        });

        // Add to the appropriate day (avoid duplicates by checking if slot already exists)
        convertedSlots.forEach((slot) => {
          const exists = weeklyAvailability[dayName].some(
            (s) => s.start === slot.start && s.end === slot.end
          );
          if (!exists) {
            weeklyAvailability[dayName].push(slot);
          }
        });
      }
    });

    // Sort slots by start time for each day
    Object.keys(weeklyAvailability).forEach((day) => {
      weeklyAvailability[day].sort((a, b) => a.start.localeCompare(b.start));
    });

    return weeklyAvailability;
  };

  // Availability management functions
  // Helper to get availability for the selected week
  const getWeekAvailability = (weekOffset = selectedWeekOffset) => {
    if (weekOffset === 0) {
      // Recurring weekly schedule
      return newService.availability;
    }
    // Week-specific override
    return newService.weeklyOverrides?.[weekOffset] || newService.availability;
  };

  // Helper to set availability for the selected week
  const setWeekAvailability = (newAvailability) => {
    if (selectedWeekOffset === 0) {
      // Update recurring schedule
      setNewService((prev) => ({
        ...prev,
        availability: newAvailability,
      }));
    } else {
      // Update week-specific override
      setNewService((prev) => ({
        ...prev,
        weeklyOverrides: {
          ...prev.weeklyOverrides,
          [selectedWeekOffset]: newAvailability,
        },
      }));
    }
  };

  // Toggle individual slot exclusion
  const toggleSlotExclusion = (day, slotKey) => {
    setNewService((prev) => {
      const dayExclusions = prev.excludedSlots[day] || [];
      const isExcluded = dayExclusions.includes(slotKey);

      return {
        ...prev,
        excludedSlots: {
          ...prev.excludedSlots,
          [day]: isExcluded
            ? dayExclusions.filter((s) => s !== slotKey)
            : [...dayExclusions, slotKey],
        },
      };
    });
  };

  // Check if a slot is excluded
  const isSlotExcluded = (day, slotStart, slotEnd) => {
    const slotKey = `${slotStart}-${slotEnd}`;
    return (newService.excludedSlots[day] || []).includes(slotKey);
  };

  // Get date range for a specific week offset
  // Week 1 = current week, Week 2 = next week, etc.
  const getWeekDateRange = (weekOffset) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
    // weekOffset 1 = current week, 2 = next week, etc.
    startOfWeek.setDate(diff + (weekOffset - 1) * 7);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    };

    return `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}`;
  };

  const addTimeSlot = (day, start, end) => {
    if (!start || !end) return;

    const newSlot = {
      id: Date.now(),
      start,
      end,
    };

    const currentAvailability = getWeekAvailability();
    const updatedAvailability = {
      ...currentAvailability,
      [day]: [...(currentAvailability[day] || []), newSlot].sort((a, b) =>
        a.start.localeCompare(b.start)
      ),
    };
    setWeekAvailability(updatedAvailability);
  };

  const removeTimeSlot = (day, slotId) => {
    const currentAvailability = getWeekAvailability();
    const updatedAvailability = {
      ...currentAvailability,
      [day]: (currentAvailability[day] || []).filter(
        (slot) => slot.id !== slotId
      ),
    };
    setWeekAvailability(updatedAvailability);
  };

  const handleAddTimeSlot = () => {
    if (newTimeSlot.start && newTimeSlot.end) {
      if (newTimeSlot.start >= newTimeSlot.end) {
        alert('End time must be after start time');
        return;
      }
      addTimeSlot(selectedDay, newTimeSlot.start, newTimeSlot.end);
      setNewTimeSlot({ start: '', end: '' }); // Clear after adding
    }
  };

  const copyAvailability = (fromDay, toDay) => {
    const currentAvailability = getWeekAvailability();
    const updatedAvailability = {
      ...currentAvailability,
      // Create new slots with unique IDs to avoid deletion conflicts
      [toDay]: (currentAvailability[fromDay] || []).map((slot, index) => ({
        ...slot,
        id: Date.now() + index, // Generate unique ID for each copied slot
      })),
    };
    setWeekAvailability(updatedAvailability);

    // Set the copied day as selected and clear after a short delay
    setSelectedCopyDay(toDay);
    setTimeout(() => {
      setSelectedCopyDay(null);
    }, 1500); // Visual feedback for 1.5 seconds
  };

  // Copy entire week schedule to another week
  const copyWeekSchedule = (fromWeekOffset, toWeekOffset) => {
    const sourceAvailability =
      fromWeekOffset === 0
        ? newService.availability
        : newService.weeklyOverrides?.[fromWeekOffset] ||
          newService.availability;

    // Create new slots with unique IDs
    const copiedAvailability = {};
    let idCounter = 0;
    Object.keys(sourceAvailability).forEach((day) => {
      copiedAvailability[day] = (sourceAvailability[day] || []).map((slot) => ({
        ...slot,
        id: Date.now() + idCounter++,
      }));
    });

    if (toWeekOffset === 0) {
      setNewService((prev) => ({
        ...prev,
        availability: copiedAvailability,
      }));
    } else {
      setNewService((prev) => ({
        ...prev,
        weeklyOverrides: {
          ...prev.weeklyOverrides,
          [toWeekOffset]: copiedAvailability,
        },
      }));
    }

    toastdisplay(
      `Schedule copied to ${toWeekOffset === 0 ? 'recurring schedule' : `Week ${toWeekOffset}`}!`,
      true,
      2000,
      setToast
    );
  };

  // Clear week-specific override (revert to recurring)
  const clearWeekOverride = (weekOffset) => {
    if (weekOffset === 0) return; // Can't clear recurring schedule

    setNewService((prev) => {
      const newOverrides = { ...prev.weeklyOverrides };
      delete newOverrides[weekOffset];
      return {
        ...prev,
        weeklyOverrides: newOverrides,
      };
    });

    toastdisplay(
      'Week override cleared. Using recurring schedule.',
      true,
      2000,
      setToast
    );
  };

  const clearDayAvailability = (day) => {
    const currentAvailability = getWeekAvailability();
    const updatedAvailability = {
      ...currentAvailability,
      [day]: [],
    };
    setWeekAvailability(updatedAvailability);
    setNewTimeSlot({ start: '', end: '' }); // Also clear the time inputs
  };

  // Helper function to render availability for a single week
  const renderWeekAvailability = (availability, weekLabel, dateRange) => {
    if (!availability) return null;

    const dayOrder = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];
    const dayNames = {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
    };

    const daysWithAvailability = dayOrder
      .filter((day) => availability[day] && availability[day].length > 0)
      .map((day) => ({
        day: dayNames[day],
        slots: availability[day].sort((a, b) =>
          (a.start || a.start_time || '').localeCompare(
            b.start || b.start_time || ''
          )
        ),
      }));

    if (daysWithAvailability.length === 0) {
      return null;
    }

    return (
      <div className="week-availability-block">
        <div className="week-header">
          <span className="week-label">{weekLabel}</span>
          <span className="week-dates">{dateRange}</span>
        </div>
        <div className="availability-days">
          {daysWithAvailability.map(({ day, slots }) => (
            <div key={day} className="availability-day">
              <span className="day-name">{day}:</span>
              <div className="day-slots">
                {slots.map((slot, index) => (
                  <span
                    key={`${day}-${slot.id || index}`}
                    className="time-slot-badge"
                  >
                    {slot.start || slot.start_time} -{' '}
                    {slot.end || slot.end_time}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper function to render service availability including weekly overrides
  const renderServiceAvailability = (service) => {
    if (!service) return null;

    const availability = service.availability;
    const weeklyOverrides =
      service.weeklyOverrides || service.weekly_overrides || {};

    // Check if we have any availability at all
    const hasRecurringAvailability =
      availability &&
      Object.entries(availability).some(
        ([key, value]) =>
          Array.isArray(value) &&
          value.length > 0 &&
          !key.match(/^\d{4}-\d{2}-\d{2}$/)
      );
    const hasWeeklyOverrides = Object.keys(weeklyOverrides).length > 0;

    if (!hasRecurringAvailability && !hasWeeklyOverrides) {
      return (
        <div className="availability-summary">
          <h6>Availability:</h6>
          <p className="no-availability-text">
            No availability configured
          </p>
        </div>
      );
    }

    // Get the max week configured
    const configuredWeeks = Object.keys(weeklyOverrides)
      .map(Number)
      .filter((n) => !isNaN(n));
    const maxWeek =
      configuredWeeks.length > 0 ? Math.max(...configuredWeeks) : 0;

    // Render weekly overrides if configured (weeks 1 to maxWeek)
    const weeklyBlocks = [];
    for (let week = 1; week <= Math.min(maxWeek, 4); week++) {
      const weekAvailability =
        weeklyOverrides[week] || weeklyOverrides[String(week)];
      if (weekAvailability) {
        const weekBlock = renderWeekAvailability(
          weekAvailability,
          `Week ${week}`,
          getWeekDateRange(week)
        );
        if (weekBlock) {
          weeklyBlocks.push(
            <div key={`week-${week}`} className="week-override-container">
              {weekBlock}
            </div>
          );
        }
      }
    }

    // Render recurring availability
    const recurringBlock = hasRecurringAvailability
      ? renderWeekAvailability(availability, 'Recurring', 'All weeks')
      : null;

    return (
      <div className="availability-summary">
        <h6>
          Availability
          {weeklyBlocks.length > 0 &&
            ` (${weeklyBlocks.length} specific week${weeklyBlocks.length > 1 ? 's' : ''})`}
          :
        </h6>

        {/* Show weekly overrides first (specific weeks) */}
        {weeklyBlocks.length > 0 && (
          <div className="weekly-overrides-section">{weeklyBlocks}</div>
        )}

        {/* Show recurring availability */}
        {recurringBlock && (
          <div className="recurring-availability-section">{recurringBlock}</div>
        )}
      </div>
    );
  };

  // Profile completion check
  const isProfileComplete = () => {
    const requiredFields = ['tagline', 'about', 'whyCoach'];

    const requiredArrays = [
      'specializedSchools',
      'sectorsOfInterest',
      'expertises',
      'lookingToMeet',
    ];

    // Check required text fields
    const hasRequiredFields = requiredFields.every(
      (field) => coachProfile[field] && coachProfile[field].trim().length > 0
    );

    // Check required arrays (at least one item each)
    const hasRequiredArrays = requiredArrays.every(
      (field) => coachProfile[field] && coachProfile[field].length > 0
    );

    // Check if at least one education entry exists
    const hasEducation =
      coachProfile.education && coachProfile.education.length > 0;

    // Check if at least one work experience entry exists
    const hasWorkExperience =
      coachProfile.workExperience && coachProfile.workExperience.length > 0;

    return (
      hasRequiredFields &&
      hasRequiredArrays &&
      hasEducation &&
      hasWorkExperience
    );
  };

  const getProfileCompletionMessage = () => {
    const missing = [];

    if (!coachProfile.tagline || coachProfile.tagline.trim().length === 0) {
      missing.push('Tagline');
    }
    if (!coachProfile.about || coachProfile.about.trim().length === 0) {
      missing.push('About section');
    }
    if (!coachProfile.whyCoach || coachProfile.whyCoach.trim().length === 0) {
      missing.push('Why I Coach section');
    }
    if (
      !coachProfile.specializedSchools ||
      coachProfile.specializedSchools.length === 0
    ) {
      missing.push('School specializations');
    }
    if (
      !coachProfile.sectorsOfInterest ||
      coachProfile.sectorsOfInterest.length === 0
    ) {
      missing.push('Sectors of interest');
    }
    if (!coachProfile.expertises || coachProfile.expertises.length === 0) {
      missing.push('Expertises');
    }
    if (
      !coachProfile.lookingToMeet ||
      coachProfile.lookingToMeet.length === 0
    ) {
      missing.push('Looking to meet targets');
    }
    if (!coachProfile.education || coachProfile.education.length === 0) {
      missing.push('Education history');
    }
    if (
      !coachProfile.workExperience ||
      coachProfile.workExperience.length === 0
    ) {
      missing.push('Work experience');
    }

    return missing;
  };

  // Get detailed profile validation errors with field identifiers for highlighting
  const getProfileValidationErrors = () => {
    const errors = [];

    if (!coachProfile.name || coachProfile.name.trim().length === 0) {
      errors.push({ field: 'name', message: 'Name is required' });
    }
    if (!coachProfile.tagline || coachProfile.tagline.trim().length === 0) {
      errors.push({
        field: 'tagline',
        message: 'Professional Tagline is required',
      });
    }
    if (!coachProfile.about || coachProfile.about.trim().length === 0) {
      errors.push({ field: 'about', message: 'About Me section is required' });
    }
    if (!coachProfile.whyCoach || coachProfile.whyCoach.trim().length === 0) {
      errors.push({
        field: 'whyCoach',
        message: 'Why I Coach section is required',
      });
    }
    if (
      !coachProfile.specializedSchools ||
      coachProfile.specializedSchools.length === 0
    ) {
      errors.push({
        field: 'specializedSchools',
        message: 'At least one school specialization is required',
      });
    }
    if (
      !coachProfile.sectorsOfInterest ||
      coachProfile.sectorsOfInterest.length === 0
    ) {
      errors.push({
        field: 'sectorsOfInterest',
        message: 'At least one sector of interest is required',
      });
    }
    if (!coachProfile.expertises || coachProfile.expertises.length === 0) {
      errors.push({
        field: 'expertises',
        message: 'At least one expertise is required',
      });
    }
    if (
      !coachProfile.lookingToMeet ||
      coachProfile.lookingToMeet.length === 0
    ) {
      errors.push({
        field: 'lookingToMeet',
        message: 'At least one "Looking to Meet" target is required',
      });
    }
    if (!coachProfile.education || coachProfile.education.length === 0) {
      errors.push({
        field: 'education',
        message: 'At least one education entry is required',
      });
    }
    if (
      !coachProfile.workExperience ||
      coachProfile.workExperience.length === 0
    ) {
      errors.push({
        field: 'workExperience',
        message: 'At least one work experience entry is required',
      });
    }

    return errors;
  };

  // Check if a specific field has validation error
  const hasFieldError = (fieldName) => {
    return profileValidationErrors.some((error) => error.field === fieldName);
  };

  // Clear validation error for a specific field
  const clearFieldError = (fieldName) => {
    setProfileValidationErrors((prev) =>
      prev.filter((error) => error.field !== fieldName)
    );
  };

  const calculateServiceEarnings = (price) => {
    const vat = price * 0.2;
    const platformFee = price * 0.15;
    const coachEarnings = price * 0.65;

    return { vat, platformFee, coachEarnings };
  };

  // Booking management functions
  const addMeetingLink = async (bookingId) => {
    const meetingLink = prompt(
      'Enter meeting link (e.g., Zoom, Google Meet, etc.):'
    );
    if (!meetingLink) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/coach/bookings/${bookingId}/meeting-link?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            meeting_link: meetingLink,
            coach_email: user.email,
          }),
        }
      );

      if (response.ok) {
        // Update local state
        setBookings((prevBookings) =>
          prevBookings.map((booking) =>
            booking.id === bookingId
              ? { ...booking, meeting_link: meetingLink }
              : booking
          )
        );
        toastdisplay('Meeting link added successfully!', true, 2000, setToast);
      } else {
        toastdisplay(
          'Failed to add meeting link. Please try again.',
          false,
          3000,
          setToast
        );
      }
    } catch (error) {
      console.error('Error adding meeting link:', error);
      toastdisplay(
        'Failed to add meeting link. Please try again.',
        false,
        3000,
        setToast
      );
    }
  };

  /**
   * Helper function to check if a conversation matches a student
   */
  const conversationMatchesStudent = (conv, studentEmail, studentName) => {
    if (!conv) return false;

    const emailLower = studentEmail.toLowerCase();
    const nameLower = studentName ? studentName.toLowerCase() : '';
    const emailUsername = emailLower.split('@')[0];

    // Check all possible email fields
    if (conv.receiver_email?.toLowerCase() === emailLower) return true;
    if (conv.email?.toLowerCase() === emailLower) return true;
    if (conv.sender_email?.toLowerCase() === emailLower) return true;
    if (conv.participant_email?.toLowerCase() === emailLower) return true;

    // Check name if provided
    if (nameLower && conv.name?.toLowerCase().includes(nameLower)) return true;

    // Check email username in name
    if (conv.name?.toLowerCase().includes(emailUsername)) return true;

    return false;
  };

  /**
   * Function for general student contact - opens messaging interface without sending automatic message
   * Used when coaches need to contact students about:
   * - Confirmed bookings (to contact student)
   * - Completed bookings (to follow up)
   * - Cancelled/No-show bookings (to contact for rescheduling)
   * This opens the messaging interface without sending any automatic messages
   * If no conversation exists, creates a new one
   */
  const contactStudent = async (
    studentEmail,
    studentName = '',
    bookingInfo = null
  ) => {
    try {
      let existingConversation = null;
      let latestConversations = conversations;

      // Always refresh from backend to get latest messages
      try {
        if (!isLoadingConversationsRef.current) {
          isLoadingConversationsRef.current = true;
          const convRes = await fetch(`${API_BASE_URL}/api/conversations`, {
            headers: getAuthHeaders(),
          });
          if (convRes.ok) {
            const data = await convRes.json();
            latestConversations = Array.isArray(data) ? data : [];
            setConversations(latestConversations);
          }
          isLoadingConversationsRef.current = false;
        }
      } catch (refreshError) {
        console.warn('Failed to refresh conversations:', refreshError);
        isLoadingConversationsRef.current = false;
      }

      // Find existing conversation using comprehensive matching
      existingConversation = latestConversations.find((conv) =>
        conversationMatchesStudent(conv, studentEmail, studentName)
      );

      // If there's a booking message, create a context message to prepend
      let bookingContextMessage = null;
      if (bookingInfo?.student_message) {
        const bookingDate = bookingInfo.booking_date
          ? formatDateWithTimezone(
              bookingInfo.booking_date,
              coachProfile.timezone
            )
          : 'their booking';
        const bookingTime = bookingInfo.start_time
          ? formatTimeWithTimezone(
              bookingInfo.start_time,
              coachProfile.timezone
            )
          : '';

        bookingContextMessage = {
          id: `booking-msg-${bookingInfo.id || Date.now()}`,
          author: studentName || studentEmail.split('@')[0],
          fromMe: false,
          text: bookingInfo.student_message,
          date: bookingInfo.created_at || new Date().toISOString(),
          displayTime: bookingTime || 'Booking message',
          isBookingMessage: true,
          bookingContext: `Message left when booking on ${bookingDate}${bookingTime ? ` at ${bookingTime}` : ''}`,
        };
      }

      // If we have an existing conversation, use it
      if (existingConversation) {
        // If there's a booking message, prepend it to the conversation if not already present
        let conversationWithContext = { ...existingConversation };
        if (bookingContextMessage) {
          const existingMessages = existingConversation.messages || [];
          // Check if the booking message is already in the conversation
          const hasBookingMessage = existingMessages.some(
            (msg) =>
              msg.isBookingMessage && msg.text === bookingContextMessage.text
          );
          if (!hasBookingMessage) {
            conversationWithContext = {
              ...existingConversation,
              messages: [bookingContextMessage, ...existingMessages],
            };
          }
        }

        // Move conversation to top of the list
        const updatedConversations = [
          conversationWithContext,
          ...latestConversations.filter(
            (conv) => conv.id !== existingConversation.id
          ),
        ];
        setConversations(updatedConversations);

        // Select this specific conversation
        setSelectedConversationForMessaging(conversationWithContext);

        // Open the messaging panel
        setShowMessagingPanel(true);

        toastdisplay(
          `Opened conversation with ${studentName || 'student'}`,
          true,
          2000,
          setToast
        );
      } else {
        // Create a new conversation with the student
        // Use a placeholder conversation object that the messaging panel can use
        const displayName = studentName || studentEmail.split('@')[0];

        // Include booking message if available
        const initialMessages = bookingContextMessage
          ? [bookingContextMessage]
          : [];

        const newConversation = {
          id: `new-${Date.now()}`,
          name: displayName,
          email: studentEmail,
          receiver_email: studentEmail,
          messages: initialMessages,
          lastMessage: bookingContextMessage?.text || '',
          date: new Date().toISOString(),
          isNewConversation: true, // Flag to indicate this is a new conversation
        };

        // Add the new conversation to the top of the list
        const updatedConversations = [newConversation, ...latestConversations];
        setConversations(updatedConversations);

        // Select this new conversation
        setSelectedConversationForMessaging(newConversation);

        // Open the messaging panel
        setShowMessagingPanel(true);

        toastdisplay(
          `Starting new conversation with ${displayName}`,
          true,
          2000,
          setToast
        );
      }
    } catch (error) {
      console.error('Error opening conversation:', error);
      toastdisplay(
        'Failed to open conversation. Please try again.',
        false,
        3000,
        setToast
      );
    }
  };

  const moveConversationToTop = (targetStudentEmail, targetStudentName) => {
    const targetConversation = conversations.find(
      (conv) =>
        conv.email === targetStudentEmail ||
        conv.name.includes(targetStudentName)
    );

    if (targetConversation) {
      const updatedConversations = [
        targetConversation,
        ...conversations.filter((conv) => conv.id !== targetConversation.id),
      ];
      setConversations(updatedConversations);
      return targetConversation;
    }
    return null;
  };

  const handleMessagingPanelClose = () => {
    setShowMessagingPanel(false);
    setSelectedConversationForMessaging(null);
  };

  /**
   * Open message modal to view full student message and reply
   */
  const openMessageModal = (booking) => {
    setSelectedBookingForMessage(booking);
    setReplyMessage('');
    setShowMessageModal(true);
  };

  /**
   * Close message modal
   */
  const closeMessageModal = () => {
    setShowMessageModal(false);
    setSelectedBookingForMessage(null);
    setReplyMessage('');
  };

  /**
   * Send reply to student's booking message
   */
  const sendReplyToStudent = async () => {
    if (!replyMessage.trim() || !selectedBookingForMessage) {
      toastdisplay('Please enter a message', false, 3000, setToast);
      return;
    }

    try {
      setSendingReply(true);

      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Send the message to the student via the messaging API
      const response = await fetch(`${API_BASE_URL}/api/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sender_email: user.email,
          receiver_email: selectedBookingForMessage.student_email,
          content: replyMessage,
          sender_timezone: userTimezone,
        }),
      });

      if (response.ok) {
        toastdisplay(
          `Reply sent to ${selectedBookingForMessage.student_name}`,
          true,
          3000,
          setToast
        );

        // Small delay to ensure backend has processed the message
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Refresh conversations to include the new message
        if (!isLoadingConversationsRef.current) {
          isLoadingConversationsRef.current = true;
          try {
            const convRes = await fetch(
              `${API_BASE_URL}/api/conversations`,
              { headers: getAuthHeaders() }
            );
            if (convRes.ok) {
              const data = await convRes.json();
              setConversations(Array.isArray(data) ? data : []);
            }
          } finally {
            isLoadingConversationsRef.current = false;
          }
        }

        closeMessageModal();
      } else {
        const errorData = await response.json();
        toastdisplay(
          errorData.detail || 'Failed to send message',
          false,
          3000,
          setToast
        );
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toastdisplay(
        'Failed to send message. Please try again.',
        false,
        3000,
        setToast
      );
    } finally {
      setSendingReply(false);
    }
  };

  /**
   * Function for payment-related messaging - creates conversation with automatic welcome message
   * Used when coaches need to contact students about payment issues or pending payments
   * This sends an automatic message to initiate the conversation
   */
  const startMessagingWithStudent = async (studentEmail, studentName) => {
    try {
      // First, try to find existing conversation using comprehensive matching
      let existingConversation = conversations.find((conv) =>
        conversationMatchesStudent(conv, studentEmail, studentName)
      );

      if (!existingConversation) {
        // If no existing conversation, create a new mentorship request/conversation first
        const response = await fetch(`${API_BASE_URL}/api/mentorship-request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            coach_email: user.email,
            student_email: studentEmail,
            message: `Hello ${studentName}, I wanted to reach out regarding your booking.`,
            initiated_by_coach: true,
          }),
        });

        if (response.ok) {
          // Refresh conversations to include the new one (only if not already loading)
          if (!isLoadingConversationsRef.current) {
            isLoadingConversationsRef.current = true;
            console.log('Refreshing conversations after creating new one...');
            const convRes = await fetch(
              `${API_BASE_URL}/api/conversations`,
              { headers: getAuthHeaders() }
            );
            if (convRes.ok) {
              const data = await convRes.json();
              setConversations(Array.isArray(data) ? data : []);

              // Try to find the new conversation
              existingConversation = data.find(
                (conv) =>
                  conv.email === studentEmail || conv.name.includes(studentName)
              );
            }
            isLoadingConversationsRef.current = false;
          }
        } else {
          throw new Error('Failed to start conversation');
        }
      }

      // If we have a conversation (existing or newly created)
      if (existingConversation) {
        // Move conversation to top of the list
        const updatedConversations = [
          existingConversation,
          ...conversations.filter(
            (conv) => conv.id !== existingConversation.id
          ),
        ];
        setConversations(updatedConversations);

        // Select this specific conversation
        setSelectedConversationForMessaging(existingConversation);

        // Open the messaging panel
        setShowMessagingPanel(true);

        toastdisplay(
          `Opened conversation with ${studentName}`,
          true,
          2000,
          setToast
        );
      } else {
        throw new Error('Failed to find or create conversation');
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      toastdisplay(
        'Failed to start conversation. Please try again.',
        false,
        3000,
        setToast
      );
    }
  };

  /**
   * Function specifically for referrals - opens messaging interface without sending automatic message
   * This allows coaches to write custom messages to their referrals instead of sending predefined ones
   */
  const startMessagingWithReferral = async (studentEmail, studentName) => {
    try {
      // First, try to find existing conversation
      let existingConversation = conversations.find(
        (conv) => conv.email === studentEmail || conv.name.includes(studentName)
      );

      if (!existingConversation) {
        // If no existing conversation, create a new empty conversation for referrals
        const response = await fetch(`${API_BASE_URL}/api/mentorship-request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            coach_email: user.email,
            student_email: studentEmail,
            message: '', // Empty message for referrals
            initiated_by_coach: true,
            is_referral: true, // Flag to indicate this is a referral conversation
          }),
        });

        if (response.ok) {
          // Refresh conversations to include the new one (only if not already loading)
          if (!isLoadingConversationsRef.current) {
            isLoadingConversationsRef.current = true;

            const convRes = await fetch(
              `${API_BASE_URL}/api/conversations`,
              { headers: getAuthHeaders() }
            );
            if (convRes.ok) {
              const data = await convRes.json();
              setConversations(Array.isArray(data) ? data : []);

              // Try to find the new conversation
              existingConversation = data.find(
                (conv) =>
                  conv.email === studentEmail || conv.name.includes(studentName)
              );
            }
            isLoadingConversationsRef.current = false;
          }
        } else {
          // If the backend doesn't support is_referral flag, try without it
          const fallbackResponse = await fetch(
            `${API_BASE_URL}/api/mentorship-request`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(),
              },
              body: JSON.stringify({
                coach_email: user.email,
                student_email: studentEmail,
                message: '', // Empty message for referrals
                initiated_by_coach: true,
              }),
            }
          );

          if (fallbackResponse.ok) {
            // Refresh conversations to include the new one (only if not already loading)
            if (!isLoadingConversationsRef.current) {
              isLoadingConversationsRef.current = true;
              const convRes = await fetch(
                `${API_BASE_URL}/api/conversations`,
                { headers: getAuthHeaders() }
              );
              if (convRes.ok) {
                const data = await convRes.json();
                setConversations(Array.isArray(data) ? data : []);

                // Try to find the new conversation
                existingConversation = data.find(
                  (conv) =>
                    conv.email === studentEmail ||
                    conv.name.includes(studentName)
                );
              }
              isLoadingConversationsRef.current = false;
            }
          } else {
            throw new Error('Failed to start conversation');
          }
        }
      }

      // If we have a conversation (existing or newly created)
      if (existingConversation) {
        // Move conversation to top of the list
        const updatedConversations = [
          existingConversation,
          ...conversations.filter(
            (conv) => conv.id !== existingConversation.id
          ),
        ];
        setConversations(updatedConversations);

        // Select this specific conversation
        setSelectedConversationForMessaging(existingConversation);

        // Open the messaging panel
        setShowMessagingPanel(true);

        toastdisplay(
          `Opened conversation with ${studentName}`,
          true,
          2000,
          setToast
        );
      } else {
        throw new Error('Failed to find or create conversation');
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      toastdisplay(
        'Failed to start conversation. Please try again.',
        false,
        3000,
        setToast
      );
    }
  };

  const updateBookingStatus = async (bookingId, newStatus, notes = '') => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/coach/bookings/${bookingId}/status?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            status: newStatus,
            coach_notes: notes,
          }),
        }
      );

      if (response.ok) {
        // Update local state
        setBookings((prevBookings) =>
          prevBookings.map((booking) =>
            booking.id === bookingId
              ? { ...booking, status: newStatus, coach_notes: notes }
              : booking
          )
        );
        toastdisplay(
          `Booking status updated to ${newStatus}!`,
          true,
          2000,
          setToast
        );
      } else {
        toastdisplay(
          'Failed to update booking status. Please try again.',
          'error'
        );
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
      toastdisplay(
        'Failed to update booking status. Please try again.',
        'error'
      );
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      CONFIRMED: { text: 'Confirmed', className: 'status-badge confirmed' },
      PENDING: {
        text: 'Pending - Payment Required',
        className: 'status-badge pending',
      },
      COMPLETED: { text: 'Completed', className: 'status-badge completed' },
      CANCELLED: { text: 'Cancelled', className: 'status-badge cancelled' },
      NO_SHOW: { text: 'No Show', className: 'status-badge cancelled' },
      AWAITING_PAYMENT: {
        text: 'Payment Required',
        className: 'status-badge pending',
      },
      // Legacy support for lowercase
      confirmed: { text: 'Confirmed', className: 'status-badge confirmed' },
      pending: {
        text: 'Pending - Payment Required',
        className: 'status-badge pending',
      },
      completed: { text: 'Completed', className: 'status-badge completed' },
      cancelled: { text: 'Cancelled', className: 'status-badge cancelled' },
      validated: { text: 'Confirmed', className: 'status-badge confirmed' },
      awaiting_payment: {
        text: 'Payment Required',
        className: 'status-badge pending',
      },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <span className={config.className}>{config.text}</span>;
  };

  const renderProfileTab = () => (
    <div className="profile-tab">
      <div className="profile-header">
        <h3>Profile Management</h3>
        <div className="profile-stats">
          <div className="coach-stat-item">
            <span className="coach-stat-number">
              {coachProfile.about ? '100' : '75'}%
            </span>
            <span className="coach-stat-label">
              Profile Complete
              <InfoTooltip
                content="Shows how much of your profile is filled out. A complete profile increases your credibility and helps students choose you as their coach."
                position="top"
                maxWidth="260px"
              />
            </span>
          </div>
          <div className="coach-stat-item">
            <span className="coach-stat-number">{services.length}</span>
            <span className="coach-stat-label">
              Services Created
              <InfoTooltip
                content="Number of coaching services you have created. More services mean more ways for students to book sessions with you."
                position="top"
                maxWidth="260px"
              />
            </span>
          </div>
        </div>
      </div>
      <div className="form-container">
        <div className="form-section">
          <h4>Basic Information</h4>
          <div className="form-row" data-field="name">
            <Form.Group
              className={`form-group ${hasFieldError('name') ? 'has-error' : ''}`}
            >
              <Form.Label>
                Name <span className="required">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={coachProfile.name}
                onChange={(e) => {
                  setCoachProfile({ ...coachProfile, name: e.target.value });
                  clearFieldError('name');
                }}
                className={hasFieldError('name') ? 'is-invalid' : ''}
                required
              />
              {hasFieldError('name') && (
                <Form.Text className="text-danger">Name is required</Form.Text>
              )}
            </Form.Group>
          </div>

          <Form.Group
            className={`form-group ${hasFieldError('tagline') ? 'has-error' : ''}`}
            data-field="tagline"
          >
            <Form.Label>
              Professional Tagline <span className="required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={coachProfile.tagline}
              onChange={(e) => {
                setCoachProfile({ ...coachProfile, tagline: e.target.value });
                clearFieldError('tagline');
              }}
              placeholder="e.g., Senior admissions consultant | Harvard Business School Alumnus"
              className={hasFieldError('tagline') ? 'is-invalid' : ''}
              required
            />
            {hasFieldError('tagline') && (
              <Form.Text className="text-danger">
                Professional Tagline is required
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group
            className={`form-group ${hasFieldError('about') ? 'has-error' : ''}`}
            data-field="about"
          >
            <Form.Label>
              About Me <span className="required">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={coachProfile.about}
              onChange={(e) => {
                setCoachProfile({ ...coachProfile, about: e.target.value });
                clearFieldError('about');
              }}
              placeholder="Tell students about your background, experience, and coaching approach..."
              className={hasFieldError('about') ? 'is-invalid' : ''}
              required
            />
            {hasFieldError('about') && (
              <Form.Text className="text-danger">
                About Me section is required
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group
            className={`form-group ${hasFieldError('whyCoach') ? 'has-error' : ''}`}
            data-field="whyCoach"
          >
            <Form.Label>
              Why I Coach <span className="required">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={coachProfile.whyCoach}
              onChange={(e) => {
                setCoachProfile({ ...coachProfile, whyCoach: e.target.value });
                clearFieldError('whyCoach');
              }}
              placeholder="What motivates you to coach? What makes you passionate about helping students achieve their goals?"
              className={hasFieldError('whyCoach') ? 'is-invalid' : ''}
              required
            />
            {hasFieldError('whyCoach') && (
              <Form.Text className="text-danger">
                Why I Coach section is required
              </Form.Text>
            )}
          </Form.Group>
        </div>

        <div className="form-section">
          <h4>Contact Information</h4>
          <div className="form-row">
            <Form.Group className="form-group">
              <Form.Label>LinkedIn Profile</Form.Label>
              <Form.Control
                type="url"
                value={coachProfile.linkedin}
                onChange={(e) =>
                  setCoachProfile({ ...coachProfile, linkedin: e.target.value })
                }
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </Form.Group>
            <Form.Group className="form-group">
              <Form.Label>Website</Form.Label>
              <Form.Control
                type="url"
                value={coachProfile.website}
                onChange={(e) =>
                  setCoachProfile({ ...coachProfile, website: e.target.value })
                }
                placeholder="https://yourwebsite.com"
              />
            </Form.Group>
          </div>
        </div>

        <div
          className={`form-section ${hasFieldError('workExperience') ? 'has-error' : ''}`}
          data-field="workExperience"
        >
          <h4>
            Work Experience <span className="required">*</span>
            {hasFieldError('workExperience') && (
              <span className="section-error-badge">Required</span>
            )}
          </h4>
          {coachProfile.workExperience.map((exp) => (
            <div key={exp.id} className="experience-item">
              <div className="form-row">
                <Form.Group className="form-group">
                  <Form.Label>
                    Company <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={exp.company}
                    onChange={(e) => {
                      updateWorkExperience(exp.id, 'company', e.target.value);
                      clearFieldError('workExperience');
                    }}
                    placeholder="Company name"
                    required
                  />
                </Form.Group>
                <Form.Group className="form-group">
                  <Form.Label>
                    Title <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={exp.title}
                    onChange={(e) =>
                      updateWorkExperience(exp.id, 'title', e.target.value)
                    }
                    placeholder="Job title"
                    required
                  />
                </Form.Group>
                <Form.Group className="form-group">
                  <Form.Label>
                    Duration <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={exp.duration}
                    onChange={(e) =>
                      updateWorkExperience(exp.id, 'duration', e.target.value)
                    }
                    placeholder="2019-2022"
                    required
                  />
                </Form.Group>
              </div>
              <Form.Group className="form-group">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={exp.description}
                  onChange={(e) =>
                    updateWorkExperience(exp.id, 'description', e.target.value)
                  }
                  placeholder="Brief description of your role and achievements"
                />
              </Form.Group>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeWorkExperience(exp.id)}
                className="remove-btn"
              >
                <FiTrash2 size={14} style={{ marginRight: '4px' }} />
                Remove
              </Button>
            </div>
          ))}
          <Button
            variant="outline-primary"
            onClick={() => {
              addWorkExperience();
              clearFieldError('workExperience');
            }}
            className="add-btn"
          >
            <FiPlus size={16} style={{ marginRight: '4px' }} />
            Add Work Experience
          </Button>
          {hasFieldError('workExperience') && (
            <Form.Text className="text-danger d-block mt-2">
              At least one work experience entry is required
            </Form.Text>
          )}
        </div>

        <div
          className={`form-section ${hasFieldError('education') ? 'has-error' : ''}`}
          data-field="education"
        >
          <h4>
            Education <span className="required">*</span>
            {hasFieldError('education') && (
              <span className="section-error-badge">Required</span>
            )}
          </h4>
          {coachProfile.education.map((edu) => (
            <div key={edu.id} className="education-item">
              <div className="form-row">
                <Form.Group className="form-group">
                  <Form.Label>
                    School <span className="required">*</span>
                  </Form.Label>
                  <Form.Select
                    value={edu.school}
                    onChange={(e) => {
                      updateEducation(edu.id, 'school', e.target.value);
                      clearFieldError('education');
                    }}
                    required
                  >
                    <option value="">Select a school</option>
                    {availableSchools.map((school) => (
                      <option key={school.id} value={school.name}>
                        {school.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="form-group">
                  <Form.Label>
                    Degree <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={edu.degree}
                    onChange={(e) =>
                      updateEducation(edu.id, 'degree', e.target.value)
                    }
                    placeholder="MBA, MS, etc."
                    required
                  />
                </Form.Group>
                <Form.Group className="form-group">
                  <Form.Label>
                    Year <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={edu.year}
                    onChange={(e) =>
                      updateEducation(edu.id, 'year', e.target.value)
                    }
                    placeholder="2019"
                    required
                  />
                </Form.Group>
              </div>
              <Form.Group className="form-group">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={edu.description}
                  onChange={(e) =>
                    updateEducation(edu.id, 'description', e.target.value)
                  }
                  placeholder="Concentration, honors, relevant details"
                />
              </Form.Group>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeEducation(edu.id)}
                className="remove-btn"
              >
                <FiTrash2 size={14} style={{ marginRight: '4px' }} />
                Remove
              </Button>
            </div>
          ))}
          <Button
            variant="outline-primary"
            onClick={() => {
              addEducation();
              clearFieldError('education');
            }}
            className="add-btn"
          >
            <FiPlus size={16} style={{ marginRight: '4px' }} />
            Add Education
          </Button>
          {hasFieldError('education') && (
            <Form.Text className="text-danger d-block mt-2">
              At least one education entry is required
            </Form.Text>
          )}
        </div>

        <div className="form-section">
          <h4>Professional Details</h4>
          <div className="form-row">
            <Form.Group className="form-group">
              <Form.Label>
                Timezone <span className="required">*</span>
              </Form.Label>
              <Form.Select
                value={coachProfile.timezone}
                onChange={(e) =>
                  setCoachProfile({ ...coachProfile, timezone: e.target.value })
                }
                required
              >
                <option value="UTC">UTC</option>
                <option value="EST">Eastern Time (EST)</option>
                <option value="PST">Pacific Time (PST)</option>
                <option value="CET">Central European Time (CET)</option>
                <option value="CEST">
                  Central European Summer Time (CEST)
                </option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="form-group">
              <Form.Label>
                Typical Response Time <span className="required">*</span>
              </Form.Label>
              <Form.Select
                value={coachProfile.responseTime}
                onChange={(e) =>
                  setCoachProfile({
                    ...coachProfile,
                    responseTime: e.target.value,
                  })
                }
                required
              >
                <option value="30 minutes">30 minutes</option>
                <option value="1 hour">1 hour</option>
                <option value="2 hours">2 hours</option>
                <option value="4 hours">4 hours</option>
                <option value="24 hours">24 hours</option>
              </Form.Select>
            </Form.Group>
          </div>
          <div className="form-row">
            <Form.Group className="form-group">
              <Form.Label>
                Minimum Booking Notice{' '}
                <InfoTooltip
                  content="Set how far in advance students must book your sessions. This gives you time to prepare and organize your schedule. For example, if set to 2 days, students can only book sessions that are at least 2 days away."
                  position="top"
                  maxWidth="280px"
                />
              </Form.Label>
              <Form.Select
                value={coachProfile.minBookingNotice}
                onChange={(e) =>
                  setCoachProfile({
                    ...coachProfile,
                    minBookingNotice: parseInt(e.target.value),
                  })
                }
              >
                <option value={0}>No minimum (same day booking allowed)</option>
                <option value={1}>1 day in advance</option>
                <option value={2}>2 days in advance</option>
                <option value={3}>3 days in advance</option>
                <option value={5}>5 days in advance</option>
                <option value={7}>1 week in advance</option>
                <option value={14}>2 weeks in advance</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Students will only see available slots that are at least{' '}
                {coachProfile.minBookingNotice === 0
                  ? 'today'
                  : coachProfile.minBookingNotice === 1
                    ? '1 day'
                    : `${coachProfile.minBookingNotice} days`}{' '}
                from now.
              </Form.Text>
            </Form.Group>
          </div>
        </div>

        <div
          className={`form-section ${hasFieldError('sectorsOfInterest') ? 'has-error' : ''}`}
          data-field="sectorsOfInterest"
        >
          <h4>
            Sectors of Interest <span className="required">*</span>
            {hasFieldError('sectorsOfInterest') && (
              <span className="section-error-badge">Required</span>
            )}
          </h4>
          <p className="form-helper-text">
            What industries or sectors are you most interested in or experienced
            with?
          </p>
          {coachProfile.sectorsOfInterest.map((sector) => (
            <div key={sector.id} className="list-item">
              <Form.Group className="form-group flex-grow">
                <Form.Control
                  type="text"
                  value={sector.name}
                  onChange={(e) => {
                    updateSectorOfInterest(sector.id, e.target.value);
                    clearFieldError('sectorsOfInterest');
                  }}
                  placeholder="e.g., Technology, Healthcare, Finance, Consulting"
                  required
                />
              </Form.Group>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeSectorOfInterest(sector.id)}
                className="remove-btn-inline"
              >
                <FiTrash2 size={14} />
              </Button>
            </div>
          ))}
          <Button
            variant="outline-primary"
            onClick={() => {
              addSectorOfInterest();
              clearFieldError('sectorsOfInterest');
            }}
            className="add-btn"
          >
            <FiPlus size={16} style={{ marginRight: '4px' }} />
            Add Sector
          </Button>
          {hasFieldError('sectorsOfInterest') && (
            <Form.Text className="text-danger d-block mt-2">
              At least one sector of interest is required
            </Form.Text>
          )}
        </div>

        <div
          className={`form-section ${hasFieldError('expertises') ? 'has-error' : ''}`}
          data-field="expertises"
        >
          <h4>
            Expertises <span className="required">*</span>
            {hasFieldError('expertises') && (
              <span className="section-error-badge">Required</span>
            )}
          </h4>
          <p className="form-helper-text">
            What are your key areas of expertise and skills?
          </p>
          {coachProfile.expertises.map((expertise) => (
            <div key={expertise.id} className="list-item">
              <Form.Group className="form-group flex-grow">
                <Form.Control
                  type="text"
                  value={expertise.name}
                  onChange={(e) => {
                    updateExpertise(expertise.id, e.target.value);
                    clearFieldError('expertises');
                  }}
                  placeholder="e.g., Strategy Consulting, Data Analytics, Leadership Development"
                  required
                />
              </Form.Group>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeExpertise(expertise.id)}
                className="remove-btn-inline"
              >
                <FiTrash2 size={14} />
              </Button>
            </div>
          ))}
          <Button
            variant="outline-primary"
            onClick={() => {
              addExpertise();
              clearFieldError('expertises');
            }}
            className="add-btn"
          >
            <FiPlus size={16} style={{ marginRight: '4px' }} />
            Add Expertise
          </Button>
          {hasFieldError('expertises') && (
            <Form.Text className="text-danger d-block mt-2">
              At least one expertise is required
            </Form.Text>
          )}
        </div>

        <div
          className={`form-section ${hasFieldError('lookingToMeet') ? 'has-error' : ''}`}
          data-field="lookingToMeet"
        >
          <h4>
            Looking to Meet <span className="required">*</span>
            {hasFieldError('lookingToMeet') && (
              <span className="section-error-badge">Required</span>
            )}
          </h4>
          <p className="form-helper-text">
            What type of students or professionals are you most interested in
            coaching?
          </p>
          {coachProfile.lookingToMeet.map((item) => (
            <div key={item.id} className="list-item">
              <Form.Group className="form-group flex-grow">
                <Form.Control
                  type="text"
                  value={item.name}
                  onChange={(e) => {
                    updateLookingToMeet(item.id, e.target.value);
                    clearFieldError('lookingToMeet');
                  }}
                  placeholder="e.g., Career Changers, Engineers, International Students"
                  required
                />
              </Form.Group>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeLookingToMeet(item.id)}
                className="remove-btn-inline"
              >
                <FiTrash2 size={14} />
              </Button>
            </div>
          ))}
          <Button
            variant="outline-primary"
            onClick={() => {
              addLookingToMeet();
              clearFieldError('lookingToMeet');
            }}
            className="add-btn"
          >
            <FiPlus size={16} style={{ marginRight: '4px' }} />
            Add Target
          </Button>
          {hasFieldError('lookingToMeet') && (
            <Form.Text className="text-danger d-block mt-2">
              At least one "Looking to Meet" target is required
            </Form.Text>
          )}
        </div>

        <div
          className={`form-section ${hasFieldError('specializedSchools') ? 'has-error' : ''}`}
          data-field="specializedSchools"
        >
          <h4>
            School Specializations <span className="required">*</span>
            {hasFieldError('specializedSchools') && (
              <span className="section-error-badge">Required</span>
            )}
          </h4>
          <p className="form-helper-text">
            Select the schools you specialize in helping students get admitted
            to:
          </p>
          <div
            className={`schools-grid ${hasFieldError('specializedSchools') ? 'has-error' : ''}`}
          >
            {availableSchools.map((school) => (
              <div
                key={school.id}
                className="school-checkbox"
                onClick={() => {
                  toggleSchoolSpecialization(school.name);
                  clearFieldError('specializedSchools');
                }}
              >
                <input
                  type="checkbox"
                  checked={coachProfile.specializedSchools.includes(
                    school.name
                  )}
                  onChange={() => {
                    toggleSchoolSpecialization(school.name);
                    clearFieldError('specializedSchools');
                  }}
                />
                <span>{school.name}</span>
              </div>
            ))}
          </div>
          {hasFieldError('specializedSchools') && (
            <Form.Text className="text-danger d-block mt-2">
              At least one school specialization is required
            </Form.Text>
          )}
        </div>

        <div className="form-actions">
          <Button
            className="btn-lwz-primary save-profile-btn"
            onClick={handleProfileSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <div
                  className="spinner-border spinner-border-sm"
                  style={{ marginRight: '8px' }}
                />
                Saving...
              </>
            ) : (
              <>
                <FiSave size={16} style={{ marginRight: '4px' }} />
                Save Profile
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  // Service status management function
  const toggleServiceStatus = async (serviceId, currentIsActive) => {
    try {
      setLoading(true);
      const newIsActive = !currentIsActive;

      const response = await fetch(
        `${API_BASE_URL}/api/coach/services/${serviceId}/status?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            is_active: Boolean(newIsActive),
          }),
        }
      );

      if (response.ok) {
        // Update local state
        const updatedServices = services.map((service) =>
          service.id === serviceId
            ? { ...service, is_active: newIsActive }
            : service
        );
        setServices(updatedServices);

        // Update localStorage with profile data only (services not cached)
        saveCoachToLocalStorage(user.email, coachProfile);

        toastdisplay(
          `Service ${newIsActive ? 'activated' : 'deactivated'} successfully!`,
          true,
          3000,
          setToast
        );
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update service status');
      }
    } catch (error) {
      console.error('Error updating service status:', error);
      toastdisplay(
        `Failed to update service status: ${error.message}`,
        false,
        3000,
        setToast
      );
    } finally {
      setLoading(false);
    }
  };

  const renderServicesTab = () => {
    // Filter services based on the selected filter
    const filteredServices = services.filter((service) => {
      if (serviceFilter === 'all') return true;
      if (serviceFilter === 'active') return service.is_active === true;
      if (serviceFilter === 'inactive') return service.is_active === false;
      return true;
    });

    return (
      <div className="services-tab">
        {/* Calendar Sync Section */}
        <div className="calendar-sync-section">
          <div className="calendar-sync-header">
            <div className="calendar-sync-info">
              <FiCalendar size={20} />
              <div>
                <h4>Calendar Synchronization</h4>
                <p>
                  Import your calendar (Google, Outlook) to avoid booking
                  conflicts
                </p>
              </div>
            </div>
            <div className="calendar-sync-actions">
              <input
                type="file"
                ref={calendarInputRef}
                accept=".ics,.ical"
                onChange={handleCalendarFileUpload}
                style={{ display: 'none' }}
                id="calendar-file-input"
              />
              {calendarFileName ? (
                <>
                  <div className="calendar-file-badge">
                    <FiCheck size={14} />
                    <span>{calendarFileName}</span>
                    <span className="event-count">
                      ({importedCalendarEvents.length} events)
                    </span>
                  </div>
                  <Button
                    className="btn-lwz-outline btn-sm"
                    onClick={() => calendarInputRef.current?.click()}
                    disabled={calendarSyncLoading}
                  >
                    <FiRefreshCw size={14} />
                    Update
                  </Button>
                  <Button
                    className="btn-lwz-secondary btn-sm"
                    onClick={clearImportedCalendar}
                    disabled={calendarSyncLoading}
                  >
                    <FiTrash2 size={14} />
                    Remove
                  </Button>
                </>
              ) : (
                <Button
                  className="btn-lwz-primary btn-sm"
                  onClick={() => calendarInputRef.current?.click()}
                  disabled={calendarSyncLoading}
                >
                  {calendarSyncLoading ? (
                    <>
                      <div className="spinner-border spinner-border-sm" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <FiUpload size={14} />
                      Import .ics file
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          {calendarFileName && (
            <div className="calendar-sync-help">
              <FiAlertCircle size={14} />
              <span>
                Time slots that conflict with your calendar will be flagged when
                creating services.
              </span>
            </div>
          )}
          <div className="conflict-legend">
            <span className="legend-title">Conflict indicators:</span>
            <div className="legend-items">
              <div className="legend-item calendar">
                <span className="legend-color"></span>
                <span>Calendar conflicts (imported events)</span>
              </div>
              <div className="legend-item service">
                <span className="legend-color"></span>
                <span>Service overlaps (other active services)</span>
              </div>
              <div className="legend-item available">
                <span className="legend-color"></span>
                <span>Available time (no conflicts)</span>
              </div>
            </div>
          </div>
          <div className="calendar-export-links">
            <small className="export-links-label">
              How to export your calendar:
            </small>
            <div className="export-links">
              <a
                href="https://support.google.com/calendar/answer/37111"
                target="_blank"
                rel="noopener noreferrer"
                className="help-link"
              >
                Google Calendar
              </a>
              <span className="link-separator">•</span>
              <a
                href="https://learn.microsoft.com/en-us/answers/questions/2281345/how-to-get-the-url-or-ics-of-a-calendar"
                target="_blank"
                rel="noopener noreferrer"
                className="help-link"
              >
                Microsoft Outlook
              </a>
              <span className="link-separator">•</span>
              <a
                href="https://smallbusiness.chron.com/convert-yahoo-calendar-outlook-72763.html"
                target="_blank"
                rel="noopener noreferrer"
                className="help-link"
              >
                Yahoo Calendar
              </a>
            </div>
          </div>
        </div>

        <div className="services-header">
          <h3>Coaching Services</h3>
          <div className="services-filters">
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="form-select"
            >
              <option value="active">Active Services</option>
              <option value="all">All Services</option>
              <option value="inactive">Inactive Services</option>
            </select>
          </div>
          <div className="services-stats">
            <div
              className={`coach-stat-item clickable ${serviceFilter === 'active' ? 'active' : ''}`}
              onClick={() => setServiceFilter('active')}
              title="Show only active services"
            >
              <span className="coach-stat-number">
                {services.filter((s) => s.is_active === true).length}
              </span>
              <span className="coach-stat-label">Active</span>
            </div>
            <div
              className={`coach-stat-item clickable ${serviceFilter === 'inactive' ? 'active' : ''}`}
              onClick={() => setServiceFilter('inactive')}
              title="Show only inactive services"
            >
              <span className="coach-stat-number">
                {services.filter((s) => s.is_active === false).length}
              </span>
              <span className="coach-stat-label">Inactive</span>
            </div>
            <div
              className={`coach-stat-item clickable ${serviceFilter === 'all' ? 'active' : ''}`}
              onClick={() => setServiceFilter('all')}
              title="Show all services"
            >
              <span className="coach-stat-number">{services.length}</span>
              <span className="coach-stat-label">Total</span>
            </div>
          </div>
          <div className="coach-template-row">
            <span className="text-muted">Start from a template</span>
            {[
              ['intro_30', 'Intro 30'],
              ['mock_60', 'Mock 60'],
              ['strategy_45', 'Strategy 45'],
            ].map(([template, label]) => (
              <Button
                key={template}
                size="sm"
                className="btn-lwz-secondary"
                onClick={() => createServiceFromTemplate(template)}
              >
                {label}
              </Button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              className="btn-lwz-outline"
              onClick={async () => {
                try {
                  setLoading(true);
                  await loadDashboardData();
                  toastdisplay(
                    'Services refreshed successfully!',
                    true,
                    2000,
                    setToast
                  );
                } catch (error) {
                  toastdisplay(
                    'Failed to refresh services',
                    false,
                    3000,
                    setToast
                  );
                } finally {
                  setLoading(false);
                }
              }}
              title="Refresh services"
              disabled={loading}
            >
              <FiRefreshCw />
              Refresh
            </Button>
            <Button
              className="btn-lwz-primary"
              onClick={() => openServiceEditor()}
            >
              <FiPlus />
              Add Service
            </Button>
          </div>
        </div>

        <div className="services-grid">
          {filteredServices.length === 0 ? (
            <div className="no-services">
              <FiBriefcase size={48} />
              <h4>
                No {serviceFilter === 'all' ? '' : serviceFilter} services yet
              </h4>
              <p>
                {serviceFilter === 'active'
                  ? 'Create your first service to start coaching students.'
                  : serviceFilter === 'inactive'
                    ? 'No inactive services found.'
                    : 'Create your first service to get started.'}
              </p>
            </div>
          ) : (
            filteredServices.map((service) => {
              const hoursLines = compactServiceHours(service);
              const takeHome = service.isFree
                ? null
                : (parseFloat(service.price || 0) * 0.65).toFixed(2);

              return (
                <div
                  key={service.id}
                  className={`service-card ${!service.is_active ? 'is-inactive' : ''}`}
                >
                  <div className="service-header">
                    <div className="service-title-section">
                      <h5>{service.title}</h5>
                      <span
                        className={`service-status-badge ${!service.is_active ? 'inactive' : 'active'}`}
                      >
                        {!service.is_active ? 'Inactive' : 'Active'}
                      </span>
                    </div>
                  </div>

                  {service.description ? (
                    <p className="service-description">{service.description}</p>
                  ) : null}

                  <div className="service-details">
                    <div className="detail-item">
                      <FiClock />
                      <span>{service.duration} min</span>
                    </div>
                    <div className="detail-item">
                      <FiDollarSign />
                      <span>
                        {service.isFree
                          ? 'Free'
                          : `€${parseFloat(service.price || 0).toFixed(2)}`}
                      </span>
                    </div>
                    <div className="detail-item">
                      <FiUsers />
                      <span>{service.bookingsCount || 0} bookings</span>
                    </div>
                  </div>

                  <div className="availability-summary compact">
                    <h6>Hours</h6>
                    {hoursLines.length > 0 ? (
                      hoursLines.map((line) => (
                        <p key={line} className="service-hours-line">
                          {line}
                        </p>
                      ))
                    ) : (
                      <p className="no-availability-text">No hours set yet</p>
                    )}
                  </div>

                  {takeHome ? (
                    <p className="service-takehome">
                      You keep €{takeHome} after fees
                    </p>
                  ) : (
                    <p className="service-takehome is-free">
                      Students can book this without paying
                    </p>
                  )}

                  <div className="service-actions">
                    <Button
                      className="btn-lwz-primary"
                      onClick={() => openServiceEditor(service)}
                    >
                      <FiEdit />
                      Edit
                    </Button>
                    <Button
                      className={`btn-lwz-${!service.is_active ? 'success' : 'secondary'} btn-sm`}
                      onClick={() =>
                        toggleServiceStatus(service.id, service.is_active)
                      }
                    >
                      {!service.is_active ? <FiCheck /> : <FiX />}
                      {!service.is_active ? 'Activate' : 'Hide'}
                    </Button>
                    <Button
                      className="btn-lwz-secondary btn-icon"
                      onClick={() => handleServiceDelete(service.id)}
                      title="Delete service"
                      aria-label="Delete service"
                    >
                      <FiTrash2 />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // Helper function to copy payment link to clipboard
  const copyPaymentLink = async (paymentLink) => {
    try {
      await navigator.clipboard.writeText(paymentLink);
      toastdisplay('Payment link copied to clipboard!', true, 3000, setToast);
    } catch (error) {
      console.error('Failed to copy payment link:', error);
      toastdisplay('Failed to copy payment link', false, 3000, setToast);
    }
  };

  // Helper functions for booking filters
  const getFilterDisplayName = (filter) => {
    switch (filter) {
      case 'confirmed':
        return 'confirmed';
      case 'pending':
        return 'pending payment';
      case 'completed':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      case 'awaiting_payment':
        return 'payment issues';
      default:
        return '';
    }
  };

  const getNoBookingsMessage = (filter) => {
    switch (filter) {
      case 'all':
        return "No bookings yet. When students book your services, they'll appear here.";
      case 'confirmed':
        return 'No confirmed bookings yet. Confirmed and validated service bookings will appear here.';
      case 'pending':
        return 'No pending bookings. Bookings awaiting payment will appear here.';
      case 'completed':
        return 'No completed bookings yet. Finished sessions will appear here.';
      case 'cancelled':
        return 'No cancelled bookings. Cancelled or no-show bookings will appear here.';
      case 'awaiting_payment':
        return 'No payment issues. Bookings with failed payments will appear here.';
      default:
        return 'No bookings found.';
    }
  };

  // Helper functions for date/time formatting with timezone
  const formatDateWithTimezone = (dateString, timezone = 'UTC') => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: timezone,
    });
  };

  const formatTimeWithTimezone = (timeString, timezone = 'UTC') => {
    try {
      // Create a date object for the time (using arbitrary date)
      const timeDate = new Date(`2000-01-01T${timeString}`);
      return timeDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
      });
    } catch (error) {
      return timeString; // fallback to original string if parsing fails
    }
  };

  const formatTimezoneDisplay = (timezone) => {
    if (!timezone) return 'UTC';

    const timezoneMap = {
      'Europe/Paris': 'CET',
      'Europe/London': 'GMT',
      'America/New_York': 'ET',
      'America/Los_Angeles': 'PT',
      'America/Chicago': 'CT',
      'Asia/Tokyo': 'JST',
      'Asia/Shanghai': 'CST',
      'Australia/Sydney': 'AET',
      UTC: 'UTC',
    };

    return timezoneMap[timezone] || timezone.split('/').pop();
  };

  const downloadCoachCalendar = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/coach/calendar.ics?user_email=${encodeURIComponent(user.email)}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error('export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'liwaza-coach.ics';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastdisplay('Could not export calendar', false, 3000, setToast);
    }
  };

  const applySharedHours = async () => {
    const recurring = {};
    sharedHours.days.forEach((day) => {
      recurring[day] = [{ start: sharedHours.start, end: sharedHours.end }];
    });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/coach/availability/copy-to-all?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ recurring }),
        }
      );
      if (!response.ok) throw new Error('copy failed');
      toastdisplay('Hours copied to all offers', true, 2500, setToast);
      await loadOnboarding();
    } catch (error) {
      toastdisplay('Could not copy hours', false, 3000, setToast);
    }
  };

  const savePayoutMethod = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/coach/payout-method?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(payoutForm),
        }
      );
      if (!response.ok) throw new Error('payout failed');
      toastdisplay('Payout method saved', true, 2500, setToast);
      await loadOnboarding();
    } catch (error) {
      toastdisplay('Could not save payout method', false, 3000, setToast);
    }
  };

  const createServiceFromTemplate = async (template) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/coach/services/from-template?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ template }),
        }
      );
      if (!response.ok) throw new Error('template failed');
      toastdisplay('Offer created from template', true, 2500, setToast);
      await loadDashboardData();
      await loadOnboarding();
    } catch (error) {
      toastdisplay('Could not create offer', false, 3000, setToast);
    }
  };

  const createIntroService = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/coach/services/intro-template?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            title: introForm.title || 'Intro 30 min',
            price: introForm.isFree ? 0 : parseFloat(introForm.price || '0'),
            is_free: introForm.isFree,
          }),
        }
      );
      if (!response.ok) throw new Error('intro failed');
      setShowIntroModal(false);
      toastdisplay('Intro offer created', true, 2500, setToast);
      await loadDashboardData();
      await loadOnboarding();
    } catch (error) {
      toastdisplay('Could not create intro offer', false, 3000, setToast);
    }
  };

  const formatGcalSyncTime = (value) => {
    if (!value) return 'Not synced yet';
    try {
      return new Date(value).toLocaleString();
    } catch (error) {
      return value;
    }
  };

  const renderCalendarTools = () => (
    <div className="coach-calendar-tools-wrap">
      <div className="gcal-connect-panel">
        <p className="gcal-readonly-copy">
          We only read busy times. Liwaza will not add events to your Google
          Calendar.
        </p>
        {gcalStatus?.connected ? (
          <div className="gcal-connected-row">
            <div>
              <strong>{gcalStatus.email || 'Google Calendar connected'}</strong>
              <small className="text-muted d-block">
                Last sync: {formatGcalSyncTime(gcalStatus.busy_synced_at)}
              </small>
              {gcalStatus.sync_error ? (
                <small className="text-danger d-block">
                  {gcalStatus.sync_error}
                </small>
              ) : null}
            </div>
            <div className="coach-calendar-tools">
              <Button
                size="sm"
                className="btn-lwz-primary"
                onClick={syncGoogleCalendar}
                disabled={gcalBusy}
              >
                <FiRefreshCw /> Sync now
              </Button>
              <Button
                size="sm"
                className="btn-lwz-secondary"
                onClick={disconnectGoogleCalendar}
                disabled={gcalBusy}
              >
                Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <Button
            size="sm"
            className="btn-lwz-primary"
            onClick={connectGoogleCalendar}
            disabled={gcalBusy || gcalStatus?.configured === false}
          >
            Connect Google Calendar
          </Button>
        )}
        {gcalStatus?.configured === false ? (
          <small className="text-muted d-block mt-1">
            Google Calendar is not configured on this server.
          </small>
        ) : null}
      </div>
      <div className="coach-calendar-tools">
        <input
          ref={calendarInputRef}
          type="file"
          accept=".ics,.ical"
          onChange={handleCalendarFileUpload}
          style={{ display: 'none' }}
        />
        <Button
          size="sm"
          variant="outline-secondary"
          className="btn-lwz-secondary"
          onClick={() => calendarInputRef.current?.click()}
          disabled={calendarSyncLoading}
        >
          <FiUpload /> Import .ics
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          className="btn-lwz-secondary"
          onClick={downloadCoachCalendar}
        >
          <FiDownload /> Export .ics
        </Button>
        {calendarFileName ? (
          <small className="text-muted">Imported: {calendarFileName}</small>
        ) : null}
      </div>
    </div>
  );

  const renderBookingsTab = () => {
    // Filter bookings based on the selected filter
    const filteredBookings = bookings.filter((booking) => {
      if (bookingFilter === 'all') return true; // Show all bookings
      if (bookingFilter === 'confirmed') {
        // Show both validated (paid) and confirmed (free) bookings
        return (
          booking.status === 'validated' ||
          booking.status === 'confirmed' ||
          booking.status === 'CONFIRMED'
        );
      }
      if (bookingFilter === 'pending')
        return booking.status === 'pending' || booking.status === 'PENDING';
      if (bookingFilter === 'completed')
        return booking.status === 'completed' || booking.status === 'COMPLETED';
      if (bookingFilter === 'cancelled')
        return (
          booking.status === 'cancelled' ||
          booking.status === 'CANCELLED' ||
          booking.status === 'NO_SHOW'
        );
      if (bookingFilter === 'awaiting_payment')
        return (
          booking.status === 'awaiting_payment' ||
          booking.status === 'AWAITING_PAYMENT'
        );
      return true;
    });

    // Apply additional filters (service, date, time slot)
    const displayBookings = filteredBookings.filter((booking) => {
      // Filter by service
      if (
        bookingServiceFilter !== 'all' &&
        booking.service_title !== bookingServiceFilter
      ) {
        return false;
      }

      // Filter by date
      if (
        bookingDateFilter !== 'all' &&
        booking.booking_date !== bookingDateFilter
      ) {
        return false;
      }

      // Filter by time slot
      if (
        bookingTimeSlotFilter !== 'all' &&
        booking.start_time !== bookingTimeSlotFilter
      ) {
        return false;
      }

      return true;
    });

    const confirmedCount = bookings.filter(
      (b) =>
        b.status === 'validated' ||
        b.status === 'confirmed' ||
        b.status === 'CONFIRMED'
    ).length;
    const pendingCount = bookings.filter(
      (b) => b.status === 'pending' || b.status === 'PENDING'
    ).length;

    return (
      <div className="bookings-tab agenda-tab">
        <div className="agenda-head">
          <div>
            <h3>Agenda</h3>
            <p>
              {displayBookings.length} session
              {displayBookings.length === 1 ? '' : 's'}
              {agendaRange === '7d'
                ? ' in the next 7 days'
                : agendaRange === '30d'
                  ? ' in the next 30 days'
                  : ''}
              {pendingCount > 0 ? ` · ${pendingCount} pending` : ''}
            </p>
          </div>
          <div className="agenda-head-actions">
            <select
              value={agendaRange}
              onChange={(e) => {
                const next = e.target.value;
                setAgendaRange(next);
                loadUpcomingBookings(next);
              }}
              className="form-select"
              aria-label="Date range"
            >
              <option value="7d">Next 7 days</option>
              <option value="30d">Next 30 days</option>
              <option value="all">All upcoming</option>
            </select>
            <select
              value={bookingFilter}
              onChange={(e) => {
                setBookingFilter(e.target.value);
                setBookingServiceFilter('all');
                setBookingDateFilter('all');
                setBookingTimeSlotFilter('all');
              }}
              className="form-select"
              aria-label="Status"
            >
              <option value="confirmed">Confirmed ({confirmedCount})</option>
              <option value="pending">Pending ({pendingCount})</option>
              <option value="all">All bookings</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="awaiting_payment">Payment issues</option>
            </select>
          </div>
        </div>

        <div className="bookings-list">
          {displayBookings.length === 0 ? (
            <div className="no-bookings">
              <FiCalendar size={48} />
              <h4>
                No{' '}
                {bookingFilter === 'all'
                  ? ''
                  : getFilterDisplayName(bookingFilter)}{' '}
                bookings yet
              </h4>
              <p>{getNoBookingsMessage(bookingFilter)}</p>
            </div>
          ) : (
            displayBookings.map((booking) => (
              <div key={booking.id} className="booking-card-lwz agenda-session">
                <div className="booking-card-lwz-header">
                  <div className="booking-card-lwz-title">
                    <h5>{booking.service_title}</h5>
                    <p>with {booking.student_name}</p>
                  </div>
                  {getStatusBadge(booking.status)}
                </div>
                <div className="agenda-session-meta">
                  <span>
                    <FiCalendar />
                    {formatDateWithTimezone(
                      booking.booking_date,
                      coachProfile.timezone
                    )}
                  </span>
                  <span>
                    <FiClock />
                    {formatTimeWithTimezone(
                      booking.start_time,
                      coachProfile.timezone
                    )}
                    –
                    {formatTimeWithTimezone(
                      booking.end_time,
                      coachProfile.timezone
                    )}
                  </span>
                  <span>
                    <FiDollarSign />
                    {booking.price_paid
                      ? `€${booking.price_paid}`
                      : 'Free'}
                  </span>
                </div>
                  {booking.student_message && (
                    <div
                      className="detail-item message-clickable"
                      onClick={() => openMessageModal(booking)}
                      title="Click to view full message and reply"
                    >
                      <FiMail />
                      <span className="message-preview">
                        <span>
                          Message:{' '}
                          {booking.student_message.length > 50
                            ? `${booking.student_message.substring(0, 50)}...`
                            : booking.student_message}
                        </span>
                        <span className="click-indicator">
                          Click to view & reply →
                        </span>
                      </span>
                    </div>
                  )}
                <div className="booking-card-lwz-actions agenda-session-actions">
                  {/* Meeting actions for confirmed/validated bookings */}
                  {(booking.status === 'validated' ||
                    booking.status === 'confirmed' ||
                    booking.status === 'CONFIRMED') && (
                    <>
                      <div className="agenda-session-actions-primary">
                        {booking.meeting_link ? (
                          <Button
                            className="btn-lwz-primary"
                            href={booking.meeting_link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FiVideo />
                            Join Meeting
                          </Button>
                        ) : (
                          <Button
                            className="btn-lwz-primary"
                            onClick={() => addMeetingLink(booking.id)}
                          >
                            <FiVideo />
                            Add Meeting Link
                          </Button>
                        )}
                        <Button
                          className="btn-lwz-secondary"
                          onClick={() =>
                            contactStudent(
                              booking.student_email,
                              booking.student_name,
                              booking
                            )
                          }
                        >
                          <FiMessageCircle />
                          Message Student
                        </Button>
                      </div>
                      <div className="agenda-session-actions-secondary">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() =>
                            updateBookingStatus(booking.id, 'COMPLETED')
                          }
                        >
                          <FiCheck /> Complete
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() =>
                            updateBookingStatus(booking.id, 'NO_SHOW')
                          }
                        >
                          No-show
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() =>
                            updateBookingStatus(booking.id, 'CANCELLED')
                          }
                        >
                          <FiX /> Cancel
                        </Button>
                      </div>
                    </>
                  )}

                  {/* Completed bookings - contact student for follow-up */}
                  {(booking.status === 'completed' ||
                    booking.status === 'COMPLETED') && (
                    <Button
                      className="btn-lwz-secondary"
                      onClick={() =>
                        contactStudent(
                          booking.student_email,
                          booking.student_name,
                          booking
                        )
                      }
                    >
                      <FiMessageCircle />
                      Follow up with Student
                    </Button>
                  )}

                  {/* Pending payment - message student about payment */}
                  {(booking.status === 'pending' ||
                    booking.status === 'PENDING') && (
                    <>
                      <Button
                        className="btn-lwz-secondary"
                        onClick={() =>
                          startMessagingWithStudent(
                            booking.student_email,
                            booking.student_name
                          )
                        }
                      >
                        <FiMessageCircle />
                        Message the student
                      </Button>
                      <Button className="btn-lwz-warning" disabled>
                        <FiClock />
                        Awaiting Payment
                      </Button>
                    </>
                  )}

                  {/* Payment issues - message student */}
                  {(booking.status === 'awaiting_payment' ||
                    booking.status === 'AWAITING_PAYMENT') && (
                    <>
                      <Button
                        className="btn-lwz-secondary"
                        onClick={() =>
                          startMessagingWithStudent(
                            booking.student_email,
                            booking.student_name
                          )
                        }
                      >
                        <FiMessageCircle />
                        Message the student
                      </Button>
                      <Button className="btn-lwz-warning" disabled>
                        <FiAlertCircle />
                        Awaiting Payment
                      </Button>
                    </>
                  )}

                  {/* Cancelled bookings - option to contact for rescheduling */}
                  {(booking.status === 'cancelled' ||
                    booking.status === 'CANCELLED' ||
                    booking.status === 'NO_SHOW') && (
                    <Button
                      className="btn-lwz-secondary"
                      onClick={() =>
                        contactStudent(
                          booking.student_email,
                          booking.student_name,
                          booking
                        )
                      }
                    >
                      <FiMessageCircle />
                      Message Student
                    </Button>
                  )}
                </div>
                {booking.coach_notes && (
                  <div className="booking-notes">
                    <small>
                      <strong>Coach Notes:</strong> {booking.coach_notes}
                    </small>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="agenda-ics-row">
          <input
            ref={calendarInputRef}
            type="file"
            accept=".ics,.ical"
            onChange={handleCalendarFileUpload}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="agenda-ics-link"
            onClick={() => calendarInputRef.current?.click()}
            disabled={calendarSyncLoading}
          >
            <FiUpload /> Import .ics
          </button>
          <button
            type="button"
            className="agenda-ics-link"
            onClick={downloadCoachCalendar}
          >
            <FiDownload /> Export .ics
          </button>
          {calendarFileName ? (
            <small>Imported: {calendarFileName}</small>
          ) : null}
        </div>
      </div>
    );
  };

  const visibleReferrals = referrals.filter((referral) => {
    if (referralFilter === 'premium') return Boolean(referral.is_premium);
    if (referralFilter === 'verified') return Boolean(referral.email_verified);
    if (referralFilter === 'linkedin') return Boolean(referral.linkedin_profile);
    return true;
  });

  const shareInviteLink = async () => {
    const shareableUrl = generateCoachInviteUrl(coachProfile.id);
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on Liwaza',
          text: 'Sign up on Liwaza with my invite link.',
          url: shareableUrl,
        });
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }
    copyShareableLink();
  };

  const renderReferralsTab = () => (
    <div className="referrals-tab">
      <div className="referrals-header">
        <h3>Your Referrals</h3>
        <div className="referrals-filters">
          <select
            value={referralFilter || 'all'}
            onChange={(e) => setReferralFilter(e.target.value)}
            className="form-select"
          >
            <option value="all">All Referrals</option>
            <option value="premium">Premium Users</option>
            <option value="verified">Verified Users</option>
            <option value="linkedin">LinkedIn Profiles</option>
          </select>
        </div>
        <div className="referrals-stats">
          <div className="coach-stat-item">
            <span className="coach-stat-number">{referrals.length}</span>
            <span className="coach-stat-label">Total Referrals</span>
          </div>
          <div className="coach-stat-item">
            <span className="coach-stat-number">
              {referrals.filter((ref) => ref.is_premium).length}
            </span>
            <span className="coach-stat-label">Premium Users</span>
          </div>
          <div className="coach-stat-item">
            <span className="coach-stat-number">
              {referrals.filter((ref) => ref.email_verified).length}
            </span>
            <span className="coach-stat-label">Verified Users</span>
          </div>
          <div className="coach-stat-item">
            <span className="coach-stat-number">
              {referrals.filter((ref) => ref.linkedin_profile).length}
            </span>
            <span className="coach-stat-label">LinkedIn Profiles</span>
          </div>
        </div>
      </div>

      {referralsLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your referrals...</p>
        </div>
      ) : referrals.length === 0 ? (
        <div className="coach-dashboard-empty-state">
          <FiUsers size={48} />
          <h4>No referrals yet</h4>
          <p>
            When users register on Liwaza using your referral link, they'll
            appear here. You can message them directly to welcome them and offer
            your coaching services.
          </p>
          <div className="d-flex gap-2">
            <Button
              className="btn-lwz-primary btn-sm"
              onClick={copyShareableLink}
            >
              Copy invite link
            </Button>
            <Button className="btn-lwz-secondary btn-sm" onClick={shareInviteLink}>
              <FiShare2 /> Share
            </Button>
          </div>
        </div>
      ) : visibleReferrals.length === 0 ? (
        <p className="text-muted">No referrals match this filter.</p>
      ) : (
        <div className="referrals-content">
          <div className="referrals-list">
            {visibleReferrals.map((referral) => (
              <div key={referral.id} className="referral-card">
                <div className="referral-status-top">
                  {referral.email_verified ? (
                    <Badge className="status-badge confirmed" bg="info">
                      Verified
                    </Badge>
                  ) : null}
                  {referral.is_premium ? (
                    <Badge bg="warning" text="dark">
                      Premium
                    </Badge>
                  ) : null}
                </div>
                <div className="referral-header">
                  <div className="referral-avatar">
                    <ProfilePicture
                      user={{
                        email: referral.email,
                        id: referral.id,
                        profile_picture: referral.profile_picture,
                        avatar: referral.avatar,
                      }}
                      alt={`${referral.first_name} ${referral.last_name}`}
                      className="avatar-image"
                    />
                  </div>
                  <div className="referral-info">
                    <h4>
                      {referral.first_name} {referral.last_name}
                    </h4>
                    <div className="referral-meta">
                      <span className="referral-date">
                        Joined{' '}
                        {new Date(referral.created_at).toLocaleDateString(
                          'en-US',
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="referral-actions">
                  <Button
                    className="btn-lwz-primary"
                    onClick={() =>
                      startMessagingWithReferral(
                        referral.email,
                        `${referral.first_name} ${referral.last_name}`
                      )
                    }
                  >
                    <FiMessageCircle /> Send Message
                  </Button>

                  {referral.linkedin_profile && (
                    <Button
                      variant="outline"
                      className="btn-lwz-secondary"
                      onClick={() =>
                        window.open(referral.linkedin_profile, '_blank')
                      }
                    >
                      <FiLinkedin /> View LinkedIn
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderEarningsTab = () => (
    <div className="earnings-tab">
      <div className="earnings-header">
        <h3>Earnings</h3>
        <p className="text-muted">
          Amounts below are already your net share. Liwaza collects via Stripe
          and marks a payout when it has been sent.
        </p>
      </div>

      <div className="earnings-cards coach-wallet-tiles">
        <div className="earnings-card">
          <div className="earnings-icon">
            <FiTrendingUp />
          </div>
          <div className="earnings-info">
            <h4>€{(earnings.thisWeek || 0).toFixed(2)}</h4>
            <p>This week</p>
          </div>
        </div>
        <div className="earnings-card">
          <div className="earnings-icon">
            <FiClock />
          </div>
          <div className="earnings-info">
            <h4>€{(earnings.pendingPayouts || 0).toFixed(2)}</h4>
            <p>Pending</p>
          </div>
        </div>
        <div className="earnings-card">
          <div className="earnings-icon">
            <FiCheck />
          </div>
          <div className="earnings-info">
            <h4>€{(earnings.paidOut || 0).toFixed(2)}</h4>
            <p>Paid</p>
          </div>
        </div>
      </div>

      <div className="earnings-breakdown">
        <h4>Transactions</h4>
        {(earnings.transactions || []).length === 0 ? (
          <p className="text-muted">No earnings yet.</p>
        ) : (
          <div className="coach-wallet-list">
            {earnings.transactions.map((row) => (
              <div key={row.id} className="coach-wallet-row">
                <div>
                  <strong>€{(row.net_amount || 0).toFixed(2)}</strong>
                  <small>
                    {row.transaction_date
                      ? new Date(row.transaction_date).toLocaleDateString()
                      : ''}
                    {row.payout_reference ? ` · ${row.payout_reference}` : ''}
                  </small>
                </div>
                <Badge bg={row.is_paid_out ? 'success' : 'warning'}>
                  {row.is_paid_out ? 'Paid' : 'Pending'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderReviewsTab = () => {
    const publishedReviews = reviews.filter(
      (review) => review.published !== false
    );
    const verifiedCount = publishedReviews.filter(
      (review) => review.verified
    ).length;
    const averageRating =
      publishedReviews.length > 0
        ? (
            publishedReviews.reduce((sum, review) => sum + review.rating, 0) /
            publishedReviews.length
          ).toFixed(1)
        : null;

    const ratingDistribution = [5, 4, 3, 2, 1].map((rating) => ({
      rating,
      count: publishedReviews.filter((r) => r.rating === rating).length,
      percentage:
        publishedReviews.length > 0
          ? (publishedReviews.filter((r) => r.rating === rating).length /
              publishedReviews.length) *
            100
          : 0,
    }));

    const renderStars = (rating) => {
      return [...Array(5)].map((_, index) => (
        <FiStar
          key={index}
          size={16}
          style={{
            color: index < rating ? '#ffc107' : '#e9ecef',
            fill: index < rating ? '#ffc107' : 'none',
          }}
        />
      ));
    };

    return (
      <div className="reviews-tab">
        <div className="reviews-header">
          <h3>Reviews</h3>
          <div className="reviews-stats">
            <div className="coach-stat-item">
              <span className="coach-stat-number">{publishedReviews.length}</span>
              <span className="coach-stat-label">
                Total reviews
                <InfoTooltip
                  content="Total number of reviews received from students. More reviews help build your reputation and attract new clients."
                  position="top"
                  maxWidth="220px"
                />
              </span>
            </div>
            <div className="coach-stat-item">
              <span className="coach-stat-number">
                {averageRating == null ? '—' : averageRating}
              </span>
              <span className="coach-stat-label">
                Average Rating
                <InfoTooltip
                  content="Your average rating out of 5, based on student feedback. High ratings indicate excellent coaching and client satisfaction."
                  position="top"
                  maxWidth="220px"
                />
              </span>
            </div>
          </div>
        </div>

        {publishedReviews.length > 0 ? (
        <div className="reviews-overview">
          <div className="rating-summary">
            <div className="average-rating">
              <h2>{averageRating}</h2>
              <div className="stars">
                {renderStars(Math.round(parseFloat(averageRating)))}
              </div>
              <p>
                Based on {publishedReviews.length} reviews ·{' '}
                {publishedReviews.length
                  ? Math.round((verifiedCount / publishedReviews.length) * 100)
                  : 0}
                % verified
              </p>
            </div>

            <div className="rating-distribution">
              {ratingDistribution.map(({ rating, count, percentage }) => (
                <div key={rating} className="rating-bar">
                  <div className="rating-label">
                    {rating} <FiStar size={12} style={{ color: '#ffc107' }} />
                  </div>
                  <div className="rating-progress">
                    <div
                      className="rating-progress-fill"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="rating-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        ) : null}

        <div className="reviews-list">
          {publishedReviews.length === 0 ? (
            <div className="no-reviews">
              <FiStar size={48} />
              <h4>Your reputation builds after the first sessions</h4>
              <p>
                Students rate you after a completed session. Share your public
                profile so the first bookings can start.
              </p>
              <Button
                className="btn-lwz-primary btn-sm"
                onClick={() =>
                  navigate(generateCoachProfileUrl(coachProfile.id))
                }
              >
                Copy public profile
              </Button>
            </div>
          ) : (
            reviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="reviewer-info">
                    <h5>{review.studentName}</h5>
                    <p className="service-title">{review.serviceTitle}</p>
                  </div>
                  <div className="review-meta">
                    <div className="rating">{renderStars(review.rating)}</div>
                    <span className="review-date">
                      {new Date(review.date).toLocaleDateString()}
                    </span>
                    {review.verified && (
                      <Badge bg="success" className="coach-verified-badge">
                        <FiCheck size={12} /> Verified
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="review-content">
                  <p>{review.comment}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderMessageModal = () => (
    <Modal
      show={showMessageModal}
      onHide={closeMessageModal}
      size="lg"
      centered
      className="student-message-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FiMail style={{ marginRight: '8px' }} />
          Message from {selectedBookingForMessage?.student_name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedBookingForMessage && (
          <>
            <Alert variant="warning" className="message-modal-context">
              <FiCalendar style={{ marginRight: '8px', flexShrink: 0 }} />
              <span>
                Regarding booking on{' '}
                <strong>
                  {formatDateWithTimezone(
                    selectedBookingForMessage.booking_date,
                    coachProfile.timezone
                  )}
                </strong>{' '}
                at{' '}
                <strong>
                  {formatTimeWithTimezone(
                    selectedBookingForMessage.start_time,
                    coachProfile.timezone
                  )}
                </strong>{' '}
                - {selectedBookingForMessage.service_title}
              </span>
            </Alert>
            <div className="message-modal-content">
              <div className="student-message-box">
                <div className="message-header">
                  <strong>{selectedBookingForMessage.student_name}</strong>
                  <small className="text-muted">
                    {selectedBookingForMessage.student_email}
                  </small>
                </div>
                <div className="message-body">
                  {selectedBookingForMessage.student_message}
                </div>
              </div>
            </div>
            <div className="reply-section">
              <Form.Group>
                <Form.Label>
                  <FiMessageCircle style={{ marginRight: '4px' }} />
                  Reply to {selectedBookingForMessage.student_name}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Type your reply here..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  disabled={sendingReply}
                />
              </Form.Group>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={closeMessageModal}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={sendReplyToStudent}
          disabled={!replyMessage.trim() || sendingReply}
        >
          {sendingReply ? (
            <>
              <FiRefreshCw
                className="spin-icon"
                style={{ marginRight: '8px' }}
              />
              Sending...
            </>
          ) : (
            <>
              <FiMail style={{ marginRight: '8px' }} />
              Send Reply
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderServiceModal = () => (
    <Modal
      show={showServiceModal}
      onHide={closeServiceEditor}
      size="lg"
      centered={!serviceModalFullscreen}
      fullscreen={serviceModalFullscreen}
      className={`service-modal-xl service-editor-modal ${serviceModalFullscreen ? 'fullscreen-modal' : ''}`}
    >
      <Modal.Header>
        <div className="service-editor-heading">
          <Modal.Title>
            {editingService ? 'Edit service' : 'New service'}
          </Modal.Title>
          <div className="service-editor-steps" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={serviceModalStep === 'details'}
              className={`service-step-tab ${serviceModalStep === 'details' ? 'active' : ''}`}
              onClick={() => setServiceModalStep('details')}
            >
              1. Details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={serviceModalStep === 'hours'}
              className={`service-step-tab ${serviceModalStep === 'hours' ? 'active' : ''} ${!isServiceDetailsValid() ? 'is-disabled' : ''}`}
              onClick={() => {
                if (isServiceDetailsValid()) setServiceModalStep('hours');
              }}
            >
              2. Hours
            </button>
          </div>
        </div>
        <div className="modal-header-actions">
          <button
            type="button"
            className="modal-action-btn"
            onClick={() => setServiceModalFullscreen(!serviceModalFullscreen)}
            title={serviceModalFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {serviceModalFullscreen ? (
              <FiMinimize2 size={18} />
            ) : (
              <FiMaximize2 size={18} />
            )}
          </button>
          <button
            type="button"
            className="modal-action-btn close-btn"
            onClick={closeServiceEditor}
            title="Close"
          >
            <FiX size={18} />
          </button>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div hidden={serviceModalStep !== 'details'}>
          <Form.Group className="mb-3">
            <Form.Label>
              Service Title <span className="required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={newService.title}
              onChange={(e) =>
                setNewService({ ...newService, title: e.target.value })
              }
              placeholder="e.g., Application Strategy Session"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Description <span className="required">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={newService.description}
              onChange={(e) =>
                setNewService({ ...newService, description: e.target.value })
              }
              placeholder="Describe what students will get from this service..."
              required
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  Duration (minutes) <span className="required">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  value={newService.duration}
                  onChange={(e) =>
                    setNewService({
                      ...newService,
                      duration: parseInt(e.target.value) || 0,
                    })
                  }
                  min="15"
                  max="240"
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Session length students see</Form.Label>
                <p className="service-editor-hint">
                  Students book this as a {newService.duration || 0}-minute
                  {newService.isFree ? ' free' : ''} session.
                </p>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="This is a free service"
              checked={newService.isFree}
              onChange={(e) =>
                setNewService({
                  ...newService,
                  isFree: e.target.checked,
                  price: e.target.checked ? '' : newService.price,
                })
              }
            />
          </Form.Group>

          {!newService.isFree && (
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>
                    Price (€) <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={newService.price}
                    onChange={(e) =>
                      setNewService({ ...newService, price: e.target.value })
                    }
                    placeholder="Enter price"
                    min="0"
                    step="0.01"
                    required={!newService.isFree}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>
                    Price Unit <span className="required">*</span>
                  </Form.Label>
                  <Form.Select
                    value={newService.priceUnit}
                    onChange={(e) =>
                      setNewService({
                        ...newService,
                        priceUnit: e.target.value,
                      })
                    }
                    required={!newService.isFree}
                  >
                    <option value="per session">per session</option>
                    <option value="per hour">per hour</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          )}

          {!newService.isFree && newService.price && (
            <Alert variant="info" className="revenue-breakdown">
              <h6>Revenue Breakdown (for €{newService.price})</h6>
              <div className="breakdown-row">
                <span>VAT (20%):</span>
                <span>€{(parseFloat(newService.price) * 0.2).toFixed(2)}</span>
              </div>
              <div className="breakdown-row">
                <span>Platform Fee (15%):</span>
                <span>€{(parseFloat(newService.price) * 0.15).toFixed(2)}</span>
              </div>
              <div className="breakdown-row earnings">
                <span>
                  <strong>Your Earnings (65%):</strong>
                </span>
                <span>
                  <strong>
                    €{(parseFloat(newService.price) * 0.65).toFixed(2)}
                  </strong>
                </span>
              </div>
            </Alert>
          )}

          <button
            type="button"
            className="service-more-toggle"
            onClick={() => setShowServiceMoreDetails((open) => !open)}
          >
            {showServiceMoreDetails ? <FiChevronUp /> : <FiChevronDown />}
            {showServiceMoreDetails ? 'Hide extra options' : 'More options'}
          </button>

          {showServiceMoreDetails && (
            <>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Break after each session (minutes)
                      <InfoTooltip
                        content="Buffer time between successive meetings. Set to 0 for back-to-back sessions."
                        position="top"
                        maxWidth="260px"
                      />
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={newService.restTime || 0}
                      onChange={(e) =>
                        setNewService({
                          ...newService,
                          restTime: parseInt(e.target.value) || 0,
                        })
                      }
                      min="0"
                      max="60"
                      placeholder="0"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Service type</Form.Label>
                    <Form.Select
                      value={newService.type}
                      onChange={(e) =>
                        setNewService({ ...newService, type: e.target.value })
                      }
                    >
                      <option value="individual">Individual session</option>
                      <option value="group">Group session</option>
                      <option value="package">Package</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
              {newService.type === 'group' && (
                <Form.Group className="mb-3">
                  <Form.Label>Maximum participants</Form.Label>
                  <Form.Control
                    type="number"
                    value={newService.maxParticipants}
                    onChange={(e) =>
                      setNewService({
                        ...newService,
                        maxParticipants: parseInt(e.target.value),
                      })
                    }
                    min="2"
                    max="20"
                  />
                </Form.Group>
              )}
            </>
          )}
          </div>

          <div hidden={serviceModalStep !== 'hours'}>
          {/* Weekly Availability Section */}
          <div className="availability-section">
            <h5 className="mb-2">Weekly hours</h5>
            <p className="text-muted mb-3">
              Turn on the days you take bookings. Students only see these times
              in {coachProfile.timezone || 'your timezone'}.
            </p>

            <div className="simple-hours">
              <div className="simple-hours-apply">
                <label>
                  From
                  <Form.Control
                    type="time"
                    value={quickHours.start}
                    onChange={(e) =>
                      setQuickHours((prev) => ({
                        ...prev,
                        start: e.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  To
                  <Form.Control
                    type="time"
                    value={quickHours.end}
                    onChange={(e) =>
                      setQuickHours((prev) => ({
                        ...prev,
                        end: e.target.value,
                      }))
                    }
                  />
                </label>
                <Button
                  className="btn-lwz-primary"
                  type="button"
                  onClick={() =>
                    applyQuickHoursToDays(
                      ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
                      quickHours.start,
                      quickHours.end
                    )
                  }
                >
                  Set Mon–Fri
                </Button>
              </div>

              <div className="simple-hours-days">
                {SERVICE_DAYS.map((day) => {
                  const slots = newService.availability?.[day] || [];
                  const on = slots.length > 0;
                  return (
                    <div
                      key={day}
                      className={`simple-hours-day ${on ? 'is-on' : ''}`}
                    >
                      <label className="simple-hours-day-toggle">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleSimpleDay(day)}
                        />
                        <span>{SERVICE_DAY_LABEL[day]}</span>
                      </label>
                      <div className="simple-hours-day-slots">
                        {on ? (
                          slots.map((slot) => (
                            <span
                              key={slot.id || `${slot.start}-${slot.end}`}
                              className="simple-hours-chip"
                            >
                              {slot.start}–{slot.end}
                              <button
                                type="button"
                                aria-label={`Remove ${SERVICE_DAY_LABEL[day]} hours`}
                                onClick={() =>
                                  removeSimpleDaySlot(day, slot.id)
                                }
                              >
                                <FiX size={12} />
                              </button>
                            </span>
                          ))
                        ) : (
                          <span className="simple-hours-off">Off</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              className="service-more-toggle"
              onClick={() => setShowAvailabilityAdvanced((open) => !open)}
            >
              {showAvailabilityAdvanced ? <FiChevronUp /> : <FiChevronDown />}
              {showAvailabilityAdvanced
                ? 'Hide week-by-week options'
                : 'Different hours for a specific week'}
            </button>

            <div hidden={!showAvailabilityAdvanced}>
            <p className="text-muted mb-3">
              Optional: override one of the next 4 weeks, or exclude individual
              booking slots.
            </p>

            {/* Week Selector */}
            <div className="week-selector-container">
              <div className="week-selector">
                <button
                  type="button"
                  className={`week-tab ${selectedWeekOffset === 0 ? 'active' : ''}`}
                  onClick={() => setSelectedWeekOffset(0)}
                >
                  <FiRepeat size={14} />
                  <span>Recurring</span>
                </button>
                {[1, 2, 3, 4].map((weekNum) => (
                  <button
                    key={weekNum}
                    type="button"
                    className={`week-tab ${selectedWeekOffset === weekNum ? 'active' : ''} ${newService.weeklyOverrides?.[weekNum] ? 'has-override' : ''}`}
                    onClick={() => setSelectedWeekOffset(weekNum)}
                  >
                    <span>Week {weekNum}</span>
                    <small>{getWeekDateRange(weekNum)}</small>
                  </button>
                ))}
              </div>

              <div className="week-actions">
                {selectedWeekOffset === 0 ? (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => copyWeekSchedule(0, 1)}
                    disabled={
                      !Object.values(newService.availability).some(
                        (slots) => slots.length > 0
                      )
                    }
                  >
                    <FiCopy size={14} />
                    Copy to Week 1
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() =>
                        copyWeekSchedule(
                          selectedWeekOffset,
                          selectedWeekOffset + 1
                        )
                      }
                      disabled={selectedWeekOffset >= 4}
                    >
                      <FiCopy size={14} />
                      Copy to Next Week
                    </Button>
                    {newService.weeklyOverrides?.[selectedWeekOffset] && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => clearWeekOverride(selectedWeekOffset)}
                      >
                        <FiRotateCcw size={14} />
                        Use Recurring
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            {selectedWeekOffset > 0 &&
              !newService.weeklyOverrides?.[selectedWeekOffset] && (
                <Alert variant="info" className="week-info-alert">
                  <FiInfo size={16} />
                  <span>
                    This week uses the recurring schedule. Make changes here to
                    create a custom schedule for this week only.
                  </span>
                </Alert>
              )}

            <div className="availability-tabs">
              {Object.keys(newService.availability).map((day) => {
                const weekAvailability = getWeekAvailability();
                const daySlots = weekAvailability[day] || [];
                return (
                  <button
                    key={day}
                    type="button"
                    className={`availability-tab ${selectedDay === day ? 'active' : ''} ${daySlots.length > 0 ? 'has-slots' : ''}`}
                    onClick={() => setSelectedDay(day)}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                    {daySlots.length > 0 && (
                      <span className="slot-count">{daySlots.length}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="availability-content">
              <div className="day-availability-header">
                <h6>
                  {selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}{' '}
                  Availability
                </h6>
                <div className="availability-actions">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => clearDayAvailability(selectedDay)}
                    disabled={
                      !(getWeekAvailability()[selectedDay] || []).length
                    }
                  >
                    Clear Day
                  </Button>
                </div>
              </div>

              <div className="time-slot-form">
                <div className="time-inputs">
                  <div className="time-picker-group">
                    <label>START</label>
                    <div className="time-picker-selects">
                      <Form.Select
                        value={
                          newTimeSlot.start
                            ? newTimeSlot.start.split(':')[0]
                            : ''
                        }
                        onChange={(e) => {
                          const minutes = newTimeSlot.start
                            ? newTimeSlot.start.split(':')[1]
                            : '00';
                          setNewTimeSlot((prev) => ({
                            ...prev,
                            start: `${e.target.value}:${minutes || '00'}`,
                          }));
                        }}
                        className="time-select-hour"
                      >
                        <option value="">--</option>
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={String(i).padStart(2, '0')}>
                            {String(i).padStart(2, '0')}
                          </option>
                        ))}
                      </Form.Select>
                      <span className="time-separator">:</span>
                      <Form.Select
                        value={
                          newTimeSlot.start
                            ? newTimeSlot.start.split(':')[1]
                            : ''
                        }
                        onChange={(e) => {
                          const hours = newTimeSlot.start
                            ? newTimeSlot.start.split(':')[0]
                            : '09';
                          setNewTimeSlot((prev) => ({
                            ...prev,
                            start: `${hours || '09'}:${e.target.value}`,
                          }));
                        }}
                        className="time-select-minute"
                      >
                        <option value="">--</option>
                        {['00', '10', '15', '20', '30', '40', '45', '50'].map(
                          (min) => (
                            <option key={min} value={min}>
                              {min}
                            </option>
                          )
                        )}
                      </Form.Select>
                    </div>
                  </div>
                  <span className="time-to-label">to</span>
                  <div className="time-picker-group">
                    <label>END</label>
                    <div className="time-picker-selects">
                      <Form.Select
                        value={
                          newTimeSlot.end ? newTimeSlot.end.split(':')[0] : ''
                        }
                        onChange={(e) => {
                          const minutes = newTimeSlot.end
                            ? newTimeSlot.end.split(':')[1]
                            : '00';
                          setNewTimeSlot((prev) => ({
                            ...prev,
                            end: `${e.target.value}:${minutes || '00'}`,
                          }));
                        }}
                        className="time-select-hour"
                      >
                        <option value="">--</option>
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={String(i).padStart(2, '0')}>
                            {String(i).padStart(2, '0')}
                          </option>
                        ))}
                      </Form.Select>
                      <span className="time-separator">:</span>
                      <Form.Select
                        value={
                          newTimeSlot.end ? newTimeSlot.end.split(':')[1] : ''
                        }
                        onChange={(e) => {
                          const hours = newTimeSlot.end
                            ? newTimeSlot.end.split(':')[0]
                            : '10';
                          setNewTimeSlot((prev) => ({
                            ...prev,
                            end: `${hours || '10'}:${e.target.value}`,
                          }));
                        }}
                        className="time-select-minute"
                      >
                        <option value="">--</option>
                        {['00', '10', '15', '20', '30', '40', '45', '50'].map(
                          (min) => (
                            <option key={min} value={min}>
                              {min}
                            </option>
                          )
                        )}
                      </Form.Select>
                    </div>
                  </div>
                  <Button
                    variant="outline-primary"
                    onClick={handleAddTimeSlot}
                    disabled={!newTimeSlot.start || !newTimeSlot.end}
                    className="add-slot-btn"
                  >
                    <FiPlus size={16} />
                    Add Slot
                  </Button>
                </div>
                {/* Preview conflict for new time slot */}
                {newTimeSlot.start &&
                  newTimeSlot.end &&
                  (() => {
                    const allConflicts = checkAllConflicts(
                      selectedDay,
                      newTimeSlot.start,
                      newTimeSlot.end
                    );
                    const freePortions = calculateFreeTimePortions(
                      selectedDay,
                      newTimeSlot.start,
                      newTimeSlot.end,
                      newService.duration || 10,
                      newService.restTime || 0
                    );

                    const totalSlots =
                      freePortions.individualSlots.length +
                      freePortions.conflictedSlots.length;
                    const activeSlots = freePortions.individualSlots.filter(
                      (s) => !isSlotExcluded(selectedDay, s.start, s.end)
                    ).length;

                    return (
                      <div className="slot-availability-container">
                        {/* Top section: Slots summary */}
                        <div className="slots-summary-section">
                          {/* Available slots to toggle */}
                          {freePortions.individualSlots.length > 0 && (
                            <div className="available-slots-section">
                              <div className="slots-header">
                                <FiCheck size={14} className="icon-success" />
                                <span className="slots-title">
                                  Click to toggle ({activeSlots} active /{' '}
                                  {freePortions.individualSlots.length}):
                                </span>
                                {(newService.excludedSlots[selectedDay] || [])
                                  .length > 0 && (
                                  <button
                                    type="button"
                                    className="clear-btn"
                                    onClick={() =>
                                      setNewService((prev) => ({
                                        ...prev,
                                        excludedSlots: {
                                          ...prev.excludedSlots,
                                          [selectedDay]: [],
                                        },
                                      }))
                                    }
                                  >
                                    <FiRotateCcw size={12} />
                                    Clear
                                  </button>
                                )}
                              </div>
                              <div className="slots-grid">
                                {freePortions.individualSlots.map(
                                  (slot, idx) => {
                                    const excluded = isSlotExcluded(
                                      selectedDay,
                                      slot.start,
                                      slot.end
                                    );
                                    return (
                                      <span
                                        key={idx}
                                        className={`slot-badge clickable ${excluded ? 'excluded' : 'available'}`}
                                        onClick={() =>
                                          toggleSlotExclusion(
                                            selectedDay,
                                            `${slot.start}-${slot.end}`
                                          )
                                        }
                                        title={
                                          excluded
                                            ? 'Click to enable'
                                            : 'Click to disable'
                                        }
                                      >
                                        {excluded && <FiX size={12} />}
                                        {slot.start} - {slot.end}
                                      </span>
                                    );
                                  }
                                )}
                              </div>
                            </div>
                          )}

                          {/* Conflicted slots */}
                          {freePortions.conflictedSlots.length > 0 && (
                            <div className="conflicted-slots-section">
                              <div className="slots-header">
                                <FiAlertCircle
                                  size={14}
                                  className="icon-conflict"
                                />
                                <span className="slots-title conflicted">
                                  Conflicted (
                                  {freePortions.conflictedSlots.length}):
                                </span>
                              </div>
                              <div className="slots-grid">
                                {freePortions.conflictedSlots.map(
                                  (slot, idx) => (
                                    <span
                                      key={idx}
                                      className="slot-badge conflicted"
                                    >
                                      {slot.start} - {slot.end}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                          {/* No slots available message */}
                          {totalSlots === 0 && (
                            <div className="no-slots-message">
                              <FiAlertCircle size={14} />
                              <span>
                                No {newService.duration || 10}-minute slots fit
                                in this window. Try a shorter duration or wider
                                time range.
                              </span>
                            </div>
                          )}

                          {/* All available message */}
                          {freePortions.individualSlots.length > 0 &&
                            freePortions.conflictedSlots.length === 0 &&
                            !allConflicts.hasAnyConflict && (
                              <div className="all-available-message">
                                <FiCheck size={14} />
                                <span>
                                  All slots in this window are available for
                                  booking
                                </span>
                              </div>
                            )}
                        </div>

                        {/* Bottom section: Conflict explanations */}
                        {allConflicts.hasAnyConflict && (
                          <div className="conflicts-explanation-section">
                            <div className="explanation-header">
                              <FiAlertTriangle size={14} />
                              <span>Why these slots are unavailable:</span>
                            </div>
                            <div className="explanation-grid">
                              {/* Calendar conflicts */}
                              {allConflicts.hasCalendarConflict && (
                                <div className="explanation-card calendar">
                                  <div className="card-header">
                                    <FiCalendar size={14} />
                                    <span>
                                      Calendar conflicts (
                                      {allConflicts.conflictingEvents.length}):
                                    </span>
                                  </div>
                                  <ul className="conflict-list">
                                    {allConflicts.conflictingEvents.map(
                                      (event, idx) => (
                                        <li key={idx}>
                                          <strong>{event.summary}</strong>
                                          <span className="time-detail">
                                            {event.date}{' '}
                                            {event.time && `(${event.time})`}
                                          </span>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              )}
                              {/* Service overlaps */}
                              {allConflicts.hasServiceConflict && (
                                <div className="explanation-card service">
                                  <div className="card-header">
                                    <FiBriefcase size={14} />
                                    <span>
                                      Service overlaps (
                                      {allConflicts.conflictingServices.length}
                                      ):
                                    </span>
                                  </div>
                                  <ul className="conflict-list">
                                    {allConflicts.conflictingServices.map(
                                      (service, idx) => (
                                        <li key={idx}>
                                          <strong>
                                            {service.serviceTitle}
                                          </strong>
                                          <span className="time-detail">
                                            ({service.time})
                                          </span>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
              </div>

              <div className="time-slots-list">
                {(() => {
                  const weekAvailability = getWeekAvailability();
                  const daySlots = weekAvailability[selectedDay] || [];

                  if (daySlots.length === 0) {
                    return (
                      <p className="no-slots">
                        No time slots set for {selectedDay}
                        {selectedWeekOffset > 0 &&
                          !newService.weeklyOverrides?.[selectedWeekOffset] &&
                          ' (using recurring schedule)'}
                      </p>
                    );
                  }

                  return daySlots.map((slot) => {
                    const allConflicts = checkAllConflicts(
                      selectedDay,
                      slot.start,
                      slot.end
                    );
                    const conflictClass =
                      allConflicts.hasCalendarConflict &&
                      allConflicts.hasServiceConflict
                        ? 'has-both-conflicts'
                        : allConflicts.hasCalendarConflict
                          ? 'has-calendar-conflict'
                          : allConflicts.hasServiceConflict
                            ? 'has-service-conflict'
                            : '';
                    const freePortions = calculateFreeTimePortions(
                      selectedDay,
                      slot.start,
                      slot.end,
                      newService.duration || 10
                    );

                    const totalSlots =
                      freePortions.individualSlots.length +
                      freePortions.conflictedSlots.length;
                    const activeSlots = freePortions.individualSlots.filter(
                      (s) => !isSlotExcluded(selectedDay, s.start, s.end)
                    ).length;

                    return (
                      <div
                        key={slot.id}
                        className={`time-slot-item ${conflictClass}`}
                      >
                        <div className="time-slot-header">
                          <span className="time-slot-time">
                            {slot.start} - {slot.end}
                          </span>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeTimeSlot(selectedDay, slot.id)}
                          >
                            <FiTrash2 size={14} />
                          </Button>
                        </div>

                        <div className="slot-availability-container compact">
                          {/* Slots section */}
                          <div className="slots-summary-section">
                            {freePortions.individualSlots.length > 0 && (
                              <div className="available-slots-section">
                                <div className="slots-header">
                                  <FiCheck size={14} className="icon-success" />
                                  <span className="slots-title">
                                    Click to toggle ({activeSlots} active /{' '}
                                    {freePortions.individualSlots.length}):
                                  </span>
                                  {(newService.excludedSlots[selectedDay] || [])
                                    .length > 0 && (
                                    <button
                                      type="button"
                                      className="clear-btn"
                                      onClick={() =>
                                        setNewService((prev) => ({
                                          ...prev,
                                          excludedSlots: {
                                            ...prev.excludedSlots,
                                            [selectedDay]: [],
                                          },
                                        }))
                                      }
                                    >
                                      <FiRotateCcw size={12} />
                                      Clear
                                    </button>
                                  )}
                                </div>
                                <div className="slots-grid">
                                  {freePortions.individualSlots.map(
                                    (individualSlot, idx) => {
                                      const excluded = isSlotExcluded(
                                        selectedDay,
                                        individualSlot.start,
                                        individualSlot.end
                                      );
                                      return (
                                        <span
                                          key={idx}
                                          className={`slot-badge clickable ${excluded ? 'excluded' : 'available'}`}
                                          onClick={() =>
                                            toggleSlotExclusion(
                                              selectedDay,
                                              `${individualSlot.start}-${individualSlot.end}`
                                            )
                                          }
                                          title={
                                            excluded
                                              ? 'Click to enable'
                                              : 'Click to disable'
                                          }
                                        >
                                          {excluded && <FiX size={12} />}
                                          {individualSlot.start} -{' '}
                                          {individualSlot.end}
                                        </span>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            )}

                            {freePortions.conflictedSlots.length > 0 && (
                              <div className="conflicted-slots-section">
                                <div className="slots-header">
                                  <FiAlertCircle
                                    size={14}
                                    className="icon-conflict"
                                  />
                                  <span className="slots-title conflicted">
                                    Conflicted (
                                    {freePortions.conflictedSlots.length}):
                                  </span>
                                </div>
                                <div className="slots-grid">
                                  {freePortions.conflictedSlots.map(
                                    (conflictedSlot, idx) => (
                                      <span
                                        key={idx}
                                        className="slot-badge conflicted"
                                      >
                                        {conflictedSlot.start} -{' '}
                                        {conflictedSlot.end}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            )}

                            {totalSlots === 0 && (
                              <div className="no-slots-message">
                                <FiAlertCircle size={14} />
                                <span>
                                  No {newService.duration || 10}-min slots fit.
                                  Adjust duration or time range.
                                </span>
                              </div>
                            )}

                            {freePortions.individualSlots.length > 0 &&
                              freePortions.conflictedSlots.length === 0 &&
                              !allConflicts.hasAnyConflict && (
                                <div className="all-available-message">
                                  <FiCheck size={14} />
                                  <span>All slots available</span>
                                </div>
                              )}
                          </div>

                          {/* Conflict explanations */}
                          {allConflicts.hasAnyConflict && (
                            <div className="conflicts-explanation-section">
                              <div className="explanation-header">
                                <FiAlertTriangle size={14} />
                                <span>Why these slots are unavailable:</span>
                              </div>
                              <div className="explanation-grid">
                                {allConflicts.hasCalendarConflict && (
                                  <div className="explanation-card calendar">
                                    <div className="card-header">
                                      <FiCalendar size={14} />
                                      <span>
                                        Calendar (
                                        {allConflicts.conflictingEvents.length}
                                        ):
                                      </span>
                                    </div>
                                    <ul className="conflict-list">
                                      {allConflicts.conflictingEvents.map(
                                        (event, idx) => (
                                          <li key={idx}>
                                            <strong>{event.summary}</strong>
                                            <span className="time-detail">
                                              {event.date}{' '}
                                              {event.time && `(${event.time})`}
                                            </span>
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                                {allConflicts.hasServiceConflict && (
                                  <div className="explanation-card service">
                                    <div className="card-header">
                                      <FiBriefcase size={14} />
                                      <span>
                                        Services (
                                        {
                                          allConflicts.conflictingServices
                                            .length
                                        }
                                        ):
                                      </span>
                                    </div>
                                    <ul className="conflict-list">
                                      {allConflicts.conflictingServices.map(
                                        (service, idx) => (
                                          <li key={idx}>
                                            <strong>
                                              {service.serviceTitle}
                                            </strong>
                                            <span className="time-detail">
                                              ({service.time})
                                            </span>
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Quick Copy Options */}
              {(() => {
                const weekAvailability = getWeekAvailability();
                const daySlots = weekAvailability[selectedDay] || [];
                return (
                  daySlots.length > 0 && (
                    <div className="copy-availability">
                      <small className="text-muted">
                        Copy this schedule to:
                      </small>
                      <div className="copy-buttons">
                        {Object.keys(newService.availability)
                          .filter((day) => day !== selectedDay)
                          .map((day) => (
                            <Button
                              key={day}
                              variant={
                                selectedCopyDay === day
                                  ? 'success'
                                  : 'outline-secondary'
                              }
                              size="sm"
                              onClick={() => copyAvailability(selectedDay, day)}
                              className={
                                selectedCopyDay === day
                                  ? 'selected-copy-btn'
                                  : ''
                              }
                            >
                              {selectedCopyDay === day ? '✓ ' : ''}
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </Button>
                          ))}
                      </div>
                    </div>
                  )
                );
              })()}
            </div>
            </div>
          </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {serviceModalStep === 'details' ? (
          <>
            <Button variant="outline-secondary" onClick={closeServiceEditor}>
              Cancel
            </Button>
            <Button
              className="btn-lwz-primary"
              onClick={() => setServiceModalStep('hours')}
              disabled={!isServiceDetailsValid()}
            >
              Continue to hours
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline-secondary"
              onClick={() => setServiceModalStep('details')}
            >
              Back
            </Button>
            <Button
              className="btn-lwz-primary"
              onClick={handleServiceSave}
              disabled={loading || !isServiceDetailsValid()}
              title={
                getServiceValidationErrors().length
                  ? `Please complete:\n• ${getServiceValidationErrors().join('\n• ')}`
                  : ''
              }
            >
              {loading
                ? 'Saving...'
                : editingService
                  ? 'Save service'
                  : 'Create service'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );

  const renderHomeTab = () => {
    const nextSession = bookings.find((booking) => {
      const status = String(booking.status || '').toUpperCase();
      return status === 'CONFIRMED' || status === 'PENDING';
    });
    const steps = [
      {
        key: 'photo',
        done: onboarding?.has_photo,
        title: 'Photo + timezone',
        action: () => navigate('/settings'),
      },
      {
        key: 'service',
        done: onboarding?.has_service,
        title: 'First service (Intro 30 min)',
        action: () => setShowIntroModal(true),
      },
      {
        key: 'hours',
        done: onboarding?.has_availability,
        title: 'Availability',
        action: () => setActiveTab('services'),
      },
      {
        key: 'payout',
        done: onboarding?.has_payout_method,
        title: 'How you want to be paid',
        action: () => {
          const el = document.getElementById('coach-payout-card');
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        },
      },
    ];
    const doneCount = steps.filter((step) => step.done).length;
    const isReviewer =
      user?.is_document_reviewer || coachProfile.isDocumentReviewer;

    return (
      <div className="coach-home">
        {!onboarding?.is_live ? (
          <section className="coach-home-card coach-onboarding-card">
            <div className="coach-home-card-head">
              <div>
                <h3>Go live in 4 steps</h3>
                <p>
                  Photo, timezone, one offer, hours, and payout. Admin can
                  complete any field for you.
                </p>
              </div>
              <span className="coach-progress-pill">
                {doneCount}/4 done
              </span>
            </div>
            <ol className="coach-checklist">
              {steps.map((step, index) => (
                <li
                  key={step.key}
                  className={step.done ? 'is-done' : ''}
                >
                  <button type="button" onClick={step.action}>
                    <span className="coach-step-index">
                      {step.done ? <FiCheck /> : index + 1}
                    </span>
                    <span className="coach-step-title">{step.title}</span>
                    <FiChevronRight />
                  </button>
                </li>
              ))}
            </ol>
          </section>
        ) : (
          <section className="coach-home-card coach-onboarding-card is-live">
            <h3>You are live</h3>
            <p>Students can book. Keep hours and payout details up to date.</p>
          </section>
        )}

        <section className="coach-home-card">
          <div className="coach-home-card-head">
            <h4>Today</h4>
            {isReviewer ? (
              <button
                type="button"
                className="coach-inline-link"
                onClick={() => navigate('/coach/document-reviews')}
              >
                <FiFileText /> Document reviews ({relectureCount})
              </button>
            ) : null}
          </div>
          {nextSession ? (
            <div className="coach-next-session">
              <div>
                <strong>{nextSession.service_title}</strong>
                <p>
                  {nextSession.student_name} · {nextSession.start_time} –{' '}
                  {nextSession.end_time}
                </p>
              </div>
              <Button
                size="sm"
                className="btn-lwz-primary"
                onClick={() => setActiveTab('bookings')}
              >
                Open agenda
              </Button>
            </div>
          ) : (
            <p className="coach-empty-copy">
              No upcoming session in this window.
            </p>
          )}
        </section>

        <section className="coach-home-card">
          <h4>Shared hours</h4>
          <p className="coach-home-help">
            Set once and copy to every offer. Week 1–8 overrides stay in the
            offer editor.
          </p>
          <div className="coach-shared-hours">
            <label className="coach-time-field">
              <span>From</span>
              <Form.Control
                type="time"
                value={sharedHours.start}
                onChange={(e) =>
                  setSharedHours((prev) => ({ ...prev, start: e.target.value }))
                }
              />
            </label>
            <label className="coach-time-field">
              <span>To</span>
              <Form.Control
                type="time"
                value={sharedHours.end}
                onChange={(e) =>
                  setSharedHours((prev) => ({ ...prev, end: e.target.value }))
                }
              />
            </label>
            <Button
              size="sm"
              className="btn-lwz-primary"
              onClick={applySharedHours}
            >
              Copy to all offers
            </Button>
          </div>
          {renderCalendarTools()}
        </section>

        <section id="coach-payout-card" className="coach-home-card">
          <h4>Payout method</h4>
          <div className="coach-payout-form">
            <label className="coach-field">
              <span>How you get paid</span>
              <Form.Select
                value={payoutForm.payout_method}
                onChange={(e) =>
                  setPayoutForm((prev) => ({
                    ...prev,
                    payout_method: e.target.value,
                  }))
                }
              >
                <option value="">Choose a method</option>
                <option value="bank_transfer">Bank transfer</option>
                <option value="mobile_money">Mobile money</option>
                <option value="other">Other</option>
              </Form.Select>
            </label>
            <label className="coach-field">
              <span>Account details</span>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="IBAN, phone, or notes"
                value={payoutForm.payout_details}
                onChange={(e) =>
                  setPayoutForm((prev) => ({
                    ...prev,
                    payout_details: e.target.value,
                  }))
                }
              />
            </label>
            <label className="coach-field coach-field-narrow">
              <span>Review SLA (business days)</span>
              <Form.Control
                type="number"
                min={1}
                max={30}
                value={payoutForm.review_sla_business_days}
                onChange={(e) =>
                  setPayoutForm((prev) => ({
                    ...prev,
                    review_sla_business_days: Number(e.target.value),
                  }))
                }
              />
            </label>
            <Button
              className="btn-lwz-primary"
              size="sm"
              onClick={savePayoutMethod}
            >
              Save payout
            </Button>
          </div>
        </section>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return renderHomeTab();
      case 'profile':
        return renderProfileTab();
      case 'services':
        return renderServicesTab();
      case 'bookings':
        return renderBookingsTab();
      case 'referrals':
        return renderReferralsTab();
      case 'earnings':
        return renderEarningsTab();
      case 'reviews':
        return renderReviewsTab();
      case 'documentReviews':
        return (
          <div>
            <h3>Document reviews</h3>
            <DocumentReviewQueue embedded />
          </div>
        );
      default:
        return renderHomeTab();
    }
  };

  if (loading && !coachProfile.name) {
    return (
      <div className="layout-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  // Function to copy shareable link to clipboard
  const copyShareableLink = () => {
    const shareableUrl = generateCoachInviteUrl(coachProfile.id);
    navigator.clipboard
      .writeText(shareableUrl)
      .then(() => {
        toastdisplay(
          'Invite link copied. Students who sign up with it are tagged as your referrals.',
          true,
          3000,
          setToast
        );
      })
      .catch((err) => {
        console.error('Failed to copy link:', err);
        toastdisplay(
          'Failed to copy link. Please try again.',
          false,
          3000,
          setToast
        );
      });
  };

  return (
    <div className="layout">
      <Sidebar />
      <CallMessaging
        showMessaging={true}
        conversations={conversations}
        userRole={user?.role.name}
        userEmail={user?.email}
        userId={user?.id}
        setConversations={setConversations}
        userName={user?.first_name + ' ' + user?.last_name}
        externalShowPanel={showMessagingPanel}
        externalSetShowPanel={setShowMessagingPanel}
        externalSelectedConversation={selectedConversationForMessaging}
        externalSetSelectedConversation={setSelectedConversationForMessaging}
        onClose={handleMessagingPanelClose}
      />

      <main className="content">
        <div className="coach-dashboard">
          <div className="dashboard-header">
            <div className="header-content">
              <h1>Coach Dashboard</h1>
              <p>Manage your coaching profile, services, and bookings.</p>
            </div>
            <div className="header-actions">
              <Button
                variant="secondary"
                className="btn-lwz-secondary"
                onClick={shareInviteLink}
              >
                <FiShare2 /> Share invite
              </Button>
              <Button
                variant="secondary"
                className="btn-lwz-secondary"
                onClick={copyShareableLink}
              >
                <FiLink /> Copy link
              </Button>
              <Button
                variant="primary"
                className="btn-lwz-primary"
                onClick={() =>
                  navigate(generateCoachProfileUrl(coachProfile.id))
                }
              >
                <FiEye /> View profile
              </Button>
            </div>
          </div>

          <div className="coach-tabs">
            <div className="nav-tabs">
              <button
                className={`nav-link ${activeTab === 'home' ? 'active' : ''}`}
                onClick={() => setActiveTab('home')}
              >
                <FiHome /> Home
              </button>
              <button
                className={`nav-link ${
                  activeTab === 'bookings' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('bookings')}
              >
                <FiCalendar /> Agenda
              </button>
              <button
                className={`nav-link ${
                  activeTab === 'referrals' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('referrals')}
              >
                <FiUsers /> Referrals
              </button>
              <button
                className={`nav-link ${
                  activeTab === 'services' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('services')}
              >
                <FiBriefcase /> Offers
              </button>
              <button
                className={`nav-link ${
                  activeTab === 'earnings' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('earnings')}
              >
                <FiDollarSign /> Earnings
              </button>
              <button
                className={`nav-link ${
                  activeTab === 'reviews' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('reviews')}
              >
                <FiStar /> Reviews
              </button>
              {user?.is_document_reviewer || coachProfile.isDocumentReviewer ? (
                <button
                  className={`nav-link ${
                    activeTab === 'documentReviews' ? 'active' : ''
                  }`}
                  onClick={() => navigate('/coach/document-reviews')}
                >
                  <FiFileText /> Document reviews
                </button>
              ) : null}
              <button
                className={`nav-link ${
                  activeTab === 'profile' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('profile')}
              >
                <FiUser /> Profile
              </button>
            </div>
          </div>

          <div className="tab-content">{renderContent()}</div>
          {renderServiceModal()}
          {renderMessageModal()}
          <Modal show={showIntroModal} onHide={() => setShowIntroModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Intro 30 min</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={introForm.title}
                  onChange={(e) =>
                    setIntroForm((prev) => ({ ...prev, title: e.target.value }))
                  }
                />
              </Form.Group>
              <Form.Check
                type="checkbox"
                label="Free session"
                checked={introForm.isFree}
                onChange={(e) =>
                  setIntroForm((prev) => ({
                    ...prev,
                    isFree: e.target.checked,
                  }))
                }
              />
              {!introForm.isFree ? (
                <Form.Group className="mt-2">
                  <Form.Label>Price (€)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={introForm.price}
                    onChange={(e) =>
                      setIntroForm((prev) => ({
                        ...prev,
                        price: e.target.value,
                      }))
                    }
                  />
                </Form.Group>
              ) : null}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => setShowIntroModal(false)}
              >
                Cancel
              </Button>
              <Button className="btn-lwz-primary" onClick={createIntroService}>
                Create offer
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Toast Notification */}
          {toast.show && (
            <div
              className={`toast-notification ${toast.success ? 'success' : 'error'}`}
            >
              <span>{toast.message}</span>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CoachDashboard;
